import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import {
  Tent,
  CalendarClock,
  CalendarCog,
  Headphones,
  Check,
  ChevronDown,
  Mail,
  MessageSquare,
  Phone,
  Settings2,
  TimerOff,
  Utensils,
  Coffee,
  X,
  BookOpen,
  ChartColumn,
  Zap,
  Search,
  Eye,
  EyeOff,
} from 'lucide-react';

import PrivateLayout from '../../layouts/PrivateLayout';
import { useUser } from '../../../contexts/UserContext';
import ConnectionsControl from './ConnectionsControl';
import { useCallCenter } from './callCenter/CallCenterProvider';
import {
  useFetchBoard,
  useFetchPotentialClients,
  useFetchCallProfile,
  useLunchCheckIn,
  useGoAway,
  useSendStepSms,
  useSendStepEmail,
  useAckDetections,
  useBoardStream,
  useTakeLead,
  useCompleteStep,
  useMarkContactMade,
  useSetRunOutcome,
  useDismissStop,
  usePushLead,
  useStartRunNow,
  useUnpushLead,
  useSaveBoardPrefs,
  useFetchBoardSchedule,
  useReassignRun,
  useReassignIntake,
  useNurtureSummary,
  useNurtureList,
  useDroppedSummary,
  useDroppedList,
} from '../../../services/onboardingBoard';
import StepAwayControl from './StepAwayControl';
import CallGuideEditor from './CallGuideEditor';
import SigningUpDesk, { deskRowsOf, leadsNeedingYou } from './potentialClients/SigningUpDesk';
import ScoreboardChip from '../workspace/ScoreboardChip';
import {
  POTENTIAL_LEGEND,
} from './potentialClients/signingUpUtils';
import DirectSignupsPanel from './potentialClients/DirectSignupsPanel';
import SigningUpGuide from './potentialClients/SigningUpGuide';
import NurtureView from './NurtureView';
import DroppedView from './DroppedView';
import PeopleSearch from './PeopleSearch';
import { cn } from '../../../lib/utils';
import AddPersonDialog, { prefillFromSearch } from './AddPersonDialog';
import TeamSettingsButton from './TeamSettingsButton';
import useAnchoredPopover from './useAnchoredPopover';
import EagleEyeButton from '../journeys/EagleEye';
import MeetingsCalendar from './potentialClients/MeetingsCalendar';
import useMeetingAlerts from './potentialClients/useMeetingAlerts';
import NudgeComposer from './potentialClients/NudgeComposer';
// Drop uses the app-wide lead delete, same as the Leads page and Lead Handoffs.
import { useDeleteLead } from '../../../services/leads';

import {
  DAY_COLUMNS,
  columnKeyForRun,
  sortByAttention,
  attentionRank,
  runDoneForToday,
  leadMatchesQuery,
} from './boardUtils';
import {
  STRIPE,
  fillFor,
  isOnLunch,
  ownerPausesOf,
  fallbackPausesOf,
  pauseLiveAt,
  pauseEndMs,
} from './slaUtils';
import { closedTodayLabel } from '../workspace/DeskKit';
import {
  fireArrivalConfetti,
  flushPendingCelebration,
  clearPendingCelebration,
} from './Celebrate';
import { BoardContexts } from './LeadTile';
import BoardColumn from './BoardColumn';
import LeadSlideOver from './LeadSlideOver';
import EventsPanel from './EventsPanel';
import ScheduleCalendar from './ScheduleCalendar';
import CallProfileEditor from './CallProfileEditor';
import TestAudioModal from './TestAudioModal';
import { isOwnedBy, NotYoursDialog, notYoursMessage } from './ownership';
import OffBoardMeetingDialog from './OffBoardMeetingDialog';
import './onboardingSupport.css';

// The stripe color language, spelled out once in the header. Red beats
// yellow beats blue: behind > act now > upcoming > quiet.
const LEGEND = [
  { color: STRIPE.upcoming, label: 'Upcoming' },
  { color: STRIPE.due, label: 'Act now' },
  { color: STRIPE.overdue, label: 'Behind' },
  { color: STRIPE.contactFresh, label: 'Day Complete' },
  { color: STRIPE.neutral, label: 'Quiet' },
];

// Contained pill so the key reads as one designed unit: labels never wrap
// mid-word, and on narrow screens the whole pill moves as a block instead of
// its items scattering.
// The header key follows the view (Nic 2026-09-22): Making Contact keeps the
// cadence key, Potential Clients shows its follow-up key. Both render in the
// SAME grid cell (the cell sizes to the wider one) and cross-fade, so
// switching views never nudges the header.
const LegendRow = ({ items, visible }) => (
  <div
    aria-hidden={!visible}
    className={`col-start-1 row-start-1 flex items-center justify-center gap-x-3 transition-opacity duration-300 motion-reduce:transition-none ${
      visible ? 'opacity-100' : 'pointer-events-none opacity-0'
    }`}
  >
    {items.map(({ color, label }) => (
      <span
        key={label}
        className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-slate-500 dark:text-slate-400"
      >
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
    ))}
  </div>
);

// The color key is reference, so it waits behind a Key button (Nic
// 2026-10-20 header redo) instead of taking the header's width all day.
// The old inline Legend stays for a quick return.
// eslint-disable-next-line no-unused-vars
const Legend = ({ view = 'contact' }) => (
  <div className="grid shrink-0 rounded-full bg-white px-3.5 py-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
    <LegendRow items={LEGEND} visible={view !== 'clients'} />
    <LegendRow items={POTENTIAL_LEGEND} visible={view === 'clients'} />
  </div>
);

const LegendKey = ({ view = 'contact' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const items = view === 'clients' ? POTENTIAL_LEGEND : LEGEND;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="What the tile colors mean"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[11px] font-medium text-slate-500 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] transition-[color] hover:text-slate-700 dark:!bg-surface-card dark:text-slate-400 dark:ring-1 dark:ring-white/10 dark:hover:text-slate-200"
      >
        <span className="flex -space-x-0.5" aria-hidden>
          {items.slice(0, 3).map((i) => (
            <span
              key={i.label}
              className="h-2 w-2 rounded-full ring-1 ring-white dark:ring-surface-card"
              style={{ backgroundColor: i.color }}
            />
          ))}
        </span>
        <span className="os-act-label hidden sm:inline">Key</span>
      </button>
      {open ? (
        <div className="absolute right-0 z-30 mt-1.5 w-52 max-w-[calc(100vw-16px)] rounded-xl bg-white p-2 shadow-lg ring-1 ring-slate-200 dark:!bg-surface-elevated dark:ring-white/10">
          <div className="px-1.5 pb-1.5 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            {view === 'clients' ? 'Signing Up colors' : 'Tile colors'}
          </div>
          {items.map(({ color, label }) => (
            <div
              key={label}
              className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 text-[13px] text-slate-700 dark:text-slate-200"
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
              {label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Workload forecast: the next 10 CALENDAR days of scheduled work across all
// my runs (a different axis than the day columns, which are cadence-days).
// Lives behind a header button so it costs zero space until asked for.
// ---------------------------------------------------------------------------

const wlTime = (d) => {
  const x = new Date(d);
  // "7 AM" when on the hour, "7:30 AM" otherwise — window ranges read
  // cleaner without the ":00" noise (Nic 2026-08-08).
  return x.toLocaleTimeString(
    [],
    x.getMinutes() === 0
      ? { hour: 'numeric' }
      : { hour: 'numeric', minute: '2-digit' },
  );
};
// The step's SLA window as a range: "7 AM - 8 AM".
const wlRange = (start, end) => `${wlTime(start)} - ${wlTime(end)}`;
const wlLongToday = () =>
  `Today is ${new Date().toLocaleDateString([], {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })}`;
const wlDateKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
};

// Header clocks: the board runs on Central time; the viewer may not.
const clockIn = (d, tz) =>
  d.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    ...(tz ? { timeZone: tz } : {}),
  });

const buildWorkload = (runs) => {
  const byKey = new Map();
  for (const run of runs) {
    if (run.state !== 'active') continue;
    for (const s of run.steps || []) {
      if (s.status !== 'pending' && s.status !== 'missed') continue;
      const at = new Date(s.windowStartAt);
      const k = wlDateKey(at);
      if (!byKey.has(k)) byKey.set(k, []);
      byKey.get(k).push({
        at,
        endAt: new Date(s.windowEndAt),
        type: s.type,
        label: s.label || s.stepKey,
        leadName: run.lead?.leadFirstName
          ? `${run.lead.leadFirstName} ${run.lead.leadLastName || ''}`.trim()
          : '',
      });
    }
  }
  const days = [];
  const today = new Date();
  for (let i = 0; i < 10; i += 1) {
    const d = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + i,
    );
    const list = (byKey.get(wlDateKey(d)) || []).sort((a, b) => a.at - b.at);
    const counts = { call: 0, sms: 0, email: 0 };
    list.forEach((s) => {
      counts[s.type] = (counts[s.type] || 0) + 1;
    });
    days.push({ date: d, list, counts, total: list.length });
  }
  return days;
};

const WL_TYPE_ICON = { call: Phone, sms: MessageSquare, email: Mail };

// ---------------------------------------------------------------------------
// Who's scheduled today — initials circles (hover = full name).
// MANAGER SPOTLIGHT (Nic 2026-08-11): with `onToggleOwner` (manage only),
// each circle is a click-filter — hide a specialist's tiles to see one
// person's board at a time. Hidden = dimmed circle; click again to restore.
// ---------------------------------------------------------------------------
export const AvatarStrip = ({
  todaySchedule = [],
  now = new Date(),
  hiddenOwners = null,
  onToggleOwner = null,
}) => {
  if (todaySchedule.length === 0) return null;
  return (
    // A LITTLE space, not a stack (Nic 2026-08-10): the lunch badge
    // overlays each circle's corner, so overlapping circles got crowded.
    <div className="-my-1.5 flex items-center gap-2.5">
      {todaySchedule.map((p) => {
        const eating = isOnLunch(
          { startAt: p.lunchStartAt, endAt: p.lunchEndAt },
          now,
        );
        // Away badges (Nic 2026-10-05): a call (2026-11-02) first, then
        // lunch, then a meeting, then a self-set break, each with its own
        // mark.
        const t = now.getTime();
        const liveOf = (kind) =>
          (p.meetingPauses || []).find(
            (x) => (x.kind || 'meeting') === kind && pauseLiveAt(x, t),
          );
        const onCall = liveOf('call');
        const inMeeting = !onCall && !eating ? liveOf('meeting') : null;
        const onBreak = !onCall && !eating && !inMeeting ? liveOf('away') : null;
        const breakWhy = onBreak
          ? onBreak.reason === 'Other' && onBreak.note
            ? onBreak.note
            : onBreak.reason
          : null;
        const hidden = !!hiddenOwners?.has(p.userId);
        // On today's schedule but outside their hours right now (Nic
        // 2026-10-14). Since 2026-10-20 that is a status BADGE (a stopwatch
        // with a slash), never a grey circle: the fill only says whether
        // their tiles are showing (Nic: two greys meant two things).
        const offShift =
          !!p.shiftStartAt &&
          !!p.shiftEndAt &&
          (t < new Date(p.shiftStartAt).getTime() ||
            t >= new Date(p.shiftEndAt).getTime());
        const shiftClock = (iso) =>
          new Date(iso).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          });
        // Hours always resolve now (day row -> usual hours -> org day,
        // 2026-09-01); "inherited" just says which rung produced them.
        const hoursText = p.startTime
          ? `${p.startTime.slice(0, 5)}-${p.endTime?.slice(0, 5)}${p.shiftInherited ? ' (usual hours)' : ''}`
          : '';
        const baseTitle = p.isOffRoster
          ? `${p.name} is no longer on the board roster but still owns leads. Open their tiles to reassign them.`
          : p.isManager
            ? `${p.name} (manager) owns leads here`
          : offShift
            ? t >= new Date(p.shiftEndAt).getTime()
              ? `${p.name} is done for today (was on ${shiftClock(p.shiftStartAt)} - ${shiftClock(p.shiftEndAt)}) and gets no new leads`
              : `${p.name} is off right now (on ${shiftClock(p.shiftStartAt)} - ${shiftClock(p.shiftEndAt)}) and gets no new leads`
          : onCall
            ? `${p.name} is on a call`
          : eating
            ? `${p.name} is at lunch until ${new Date(p.lunchEndAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
            : inMeeting
              ? `${p.name} is in a meeting. Back by ${new Date(inMeeting.endAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
              : onBreak
                ? `${p.name} is away${breakWhy ? ` (${breakWhy})` : ''} until ${new Date(onBreak.endAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                : `${p.name} · ${hoursText}`;
        const circle = (
          <span
            title={
              onToggleOwner
                ? `${baseTitle}\n${hidden ? 'Hidden. Click to show their tiles again' : 'Click to hide their tiles (focus on the others)'}`
                : baseTitle
            }
            // The fill is the toggle only: solid = their tiles show, a
            // hollow outline = toggled off (a different shape, not another
            // grey). Status lives in the corner badge.
            className={`flex h-9 w-9 items-center justify-center rounded-full text-[12px] font-bold ring-2 ring-white transition-colors dark:ring-surface-page ${
              p.isOffRoster
                ? // Off the roster (role removed / deactivated) but still
                  // owning leads: greyed + dashed so it reads as "former".
                  'border border-dashed border-slate-400 bg-slate-100 text-slate-400 dark:border-slate-500 dark:!bg-white/5 dark:text-slate-500'
                : hidden
                  ? 'border-2 border-slate-300 bg-white text-slate-400 dark:border-white/25 dark:!bg-transparent dark:!text-slate-500'
                  : 'bg-slate-800 text-white dark:bg-slate-200 dark:!text-slate-900'
            }`}
          >
            {p.initials}
          </span>
        );
        return (
          <span key={p.userId} className="relative inline-flex">
            {onToggleOwner ? (
              <button
                type="button"
                onClick={() => onToggleOwner(p.userId)}
                className="inline-flex rounded-full"
              >
                {circle}
              </button>
            ) : (
              circle
            )}
            {/* Status badges: off the clock (grey stopwatch, slashed), a
                call (amber phone), lunch (violet fork), a meeting (teal), a
                break (sky cup). Full strength whether the circle is toggled
                on or off. */}
            {offShift ? (
              <span
                title={baseTitle}
                className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-slate-500 text-white ring-2 ring-white dark:bg-slate-600 dark:ring-surface-page"
                style={{ height: 20, width: 20 }}
              >
                <TimerOff size={11} />
              </span>
            ) : onCall ? (
              <span
                title={baseTitle}
                className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-amber-500 text-white ring-2 ring-white dark:ring-surface-page"
                style={{ height: 20, width: 20 }}
              >
                <Phone size={11} />
              </span>
            ) : eating ? (
              <span
                title={`${p.name} is at lunch`}
                className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-violet-500 text-white ring-2 ring-white dark:ring-surface-page"
                style={{ height: 20, width: 20 }}
              >
                <Utensils size={11} />
              </span>
            ) : inMeeting || onBreak ? (
              <span
                title={baseTitle}
                className={`absolute -bottom-1 -right-1 flex items-center justify-center rounded-full text-white ring-2 ring-white dark:ring-surface-page ${
                  inMeeting ? 'bg-teal-500' : 'bg-sky-500'
                }`}
                style={{ height: 20, width: 20 }}
              >
                {inMeeting ? <CalendarClock size={11} /> : <Coffee size={11} />}
              </span>
            ) : null}
          </span>
        );
      })}
      {/* Off-roster owners exist only in the manager payload (2026-09-01):
          say it in words too, since a greyed circle alone is easy to miss. */}
      {todaySchedule.some((p) => p.isOffRoster) ? (
        <span
          className="ml-1 text-[10px] font-semibold text-amber-600 dark:text-amber-400"
          title="Former specialists who still own leads. Open their tiles to reassign."
        >
          {todaySchedule.filter((p) => p.isOffRoster).length} off roster
        </span>
      ) : null}
    </div>
  );
};

// The lunch pill (2026-08-10) is gone (Nic 2026-10-20): the banner says it
// during lunch.

// Hidden from the header since 2026-10-05 (Nic), kept for a quick return.
// eslint-disable-next-line no-unused-vars
const WorkloadPanel = ({ runs }) => {
  const [open, setOpen] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const popStyle = useAnchoredPopover(open, btnRef, 320);
  const days = useMemo(() => buildWorkload(runs), [runs]);
  const todayTotal = days[0]?.total || 0;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const dayLabel = (d, i) =>
    i === 0
      ? 'Today'
      : i === 1
        ? 'Tomorrow'
        : d.toLocaleDateString([], {
            weekday: 'short',
            month: 'numeric',
            day: 'numeric',
          });

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Workload: your next 10 days"
        className={`flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[11px] font-medium shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] transition-colors dark:!bg-surface-card dark:ring-1 dark:ring-white/10 ${
          open
            ? 'text-slate-800 dark:text-slate-100'
            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
        }`}
      >
        <ChartColumn size={13} />
        <span className="hidden sm:inline">Workload</span>
        {todayTotal > 0 ? (
          <span className="rounded-full bg-slate-900 px-1.5 py-px text-[10px] font-semibold text-white dark:bg-slate-200 dark:!text-slate-900">
            {todayTotal}
          </span>
        ) : null}
      </button>
      {open && popStyle ? (
        <div
          style={popStyle}
          className="z-30 rounded-xl bg-white p-2 shadow-[0_4px_12px_rgba(15,23,42,0.08),0_12px_32px_rgba(15,23,42,0.12)] dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10"
        >
          <div className="flex items-center justify-between px-2.5 pb-1 pt-1.5">
            <div>
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                {wlLongToday()}
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Next 10 days
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
            >
              <X size={13} />
            </button>
          </div>
          <div className="os-scroll max-h-96 overflow-y-auto">
            {days.map((day, i) => {
              const k = wlDateKey(day.date);
              const isExpanded = expandedKey === k;
              return (
                <div key={k}>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedKey(isExpanded || day.total === 0 ? null : k)
                    }
                    className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-white/5 ${
                      i === 0 ? 'bg-slate-50/70 dark:!bg-white/5' : ''
                    }`}
                  >
                    <span
                      className={`w-20 shrink-0 text-xs ${
                        i === 0
                          ? 'font-semibold text-slate-800 dark:text-slate-100'
                          : 'font-medium text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {dayLabel(day.date, i)}
                    </span>
                    {day.total === 0 ? (
                      <span className="text-xs text-slate-300 dark:text-slate-600">
                        free
                      </span>
                    ) : (
                      <span className="flex items-center gap-2.5 text-xs text-slate-500 dark:text-slate-400">
                        {['call', 'sms', 'email'].map((t) => {
                          if (!day.counts[t]) return null;
                          const Icon = WL_TYPE_ICON[t];
                          return (
                            <span
                              key={t}
                              className="inline-flex items-center gap-1"
                            >
                              <Icon size={11} className="text-slate-400" />
                              {day.counts[t]}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </button>
                  {isExpanded ? (
                    // Flush with the day label, not indented past it (Nic
                    // 2026-08-24): the old 5.5rem indent plus the time
                    // column pushed the step + lead name into truncation.
                    <div className="mb-1 ml-2.5 mr-2 space-y-0.5 pb-1">
                      {day.list.map((s, j) => (
                        <div
                          key={j}
                          className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400"
                        >
                          <span className="w-24 shrink-0 tabular-nums text-slate-400 dark:text-slate-500">
                            {wlRange(s.at, s.endAt)}
                          </span>
                          <span className="truncate">
                            {s.label} · {s.leadName}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Per-user display settings behind a quiet gear (2026-08-06). Choices save
// to the server on click (users.userBoardPrefs) so they follow the person
// across devices. Settings render as an ACCORDION (2026-08-06): each setting
// is a clickable header row, one submenu open at a time.
// Shared with Client Onboarding's Display menu (Nic 2026-10-10).
export const TILE_STYLE_OPTIONS = [
  {
    value: 'classic',
    label: 'Classic',
    caption: 'Colored edge',
  },
  {
    value: 'solid',
    label: 'Solid',
    caption: 'Colored tile',
  },
];

export const THEME_OPTIONS = [
  {
    value: 'light',
    label: 'Light',
    caption: 'Bright canvas',
  },
  {
    value: 'dark',
    label: 'Dark',
    caption: 'Dark canvas',
  },
];

const CELEBRATE_OPTIONS = [
  {
    value: true,
    label: 'On',
    caption: 'Confetti',
  },
  {
    value: false,
    label: 'Off',
    caption: 'No confetti',
  },
];

// Hide done (Nic 2026-10-31): an eye beside the search on Making Contact,
// never a menu option. Buried in the gear (2026-10-20) it hid tiles with no
// hint that anything was hidden: a Saturday reads every Friday step as
// closed, so whole columns went empty. On, the eye is slashed and carries
// the number of tiles it is hiding, so an empty column is never a mystery.
const HideDoneToggle = ({ on, hidden, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={on}
    title={
      on
        ? `Hiding ${hidden} ${hidden === 1 ? 'tile' : 'tiles'} done for today. Click to show every tile.`
        : 'Showing every tile. Click to hide the ones done for today.'
    }
    className={cn(
      // A fixed circle the size of the search box, never wider: the count
      // rides its corner and the on state is an inset ring, so nothing grows.
      'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white outline-none transition-[color,box-shadow] focus:outline-none dark:!bg-white/5',
      on
        ? 'text-sky-700 shadow-[inset_0_0_0_1.5px_rgba(14,165,233,0.85)] dark:text-sky-300'
        : 'text-slate-500 ring-1 ring-slate-200 hover:text-slate-700 dark:text-slate-400 dark:ring-white/10 dark:hover:text-slate-200',
    )}
  >
    {on ? <EyeOff size={15} /> : <Eye size={15} />}
    {on && hidden > 0 ? (
      <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-500 px-1 text-[9px] font-bold tabular-nums text-white ring-2 ring-white dark:ring-surface-page">
        {hidden}
      </span>
    ) : null}
  </button>
);

// Mini tile previews so the choice reads visually, not just by name.
export const TileStylePreview = ({ value }) =>
  value === 'classic' ? (
    <span className="flex h-8 w-12 shrink-0 items-center rounded-md border-[1px] border-slate-200 bg-white shadow-sm dark:border-slate-600 dark:!bg-slate-200">
      <span
        className="h-full w-1 rounded-l-md"
        style={{ background: STRIPE.due }}
      />
      <span className="ml-1.5 flex flex-col gap-1">
        <span className="h-1 w-6 rounded bg-slate-300" />
        <span className="h-1 w-4 rounded bg-slate-200 dark:bg-slate-300" />
      </span>
    </span>
  ) : (
    <span
      className="flex h-8 w-12 shrink-0 flex-col justify-center gap-1 rounded-md pl-2 shadow-sm"
      style={{ background: fillFor(STRIPE.due) }}
    >
      <span className="h-1 w-6 rounded bg-white/80" />
      <span className="h-1 w-4 rounded bg-white/50" />
    </span>
  );

// Mini board swatch per theme: tiny canvas + two striped tile bars, rendered
// in that theme's actual surfaces so the choice is visual.
export const ThemePreview = ({ value }) => {
  const dark = value === 'dark';
  return (
    <span
      className={`flex h-8 w-12 shrink-0 flex-col justify-center gap-1 rounded-md border px-1.5 shadow-sm ${
        dark ? 'border-slate-600 bg-[#0E121B]' : 'border-slate-200 bg-[#F5F7FA]'
      }`}
    >
      {[STRIPE.due, STRIPE.upcoming].map((stripe, i) => (
        <span
          key={stripe}
          className={`flex h-2 items-center overflow-hidden rounded-sm ${
            dark ? 'bg-[#222530]' : 'border border-slate-200/80 bg-white'
          } ${i === 1 ? 'w-3/4' : 'w-full'}`}
        >
          <span
            className="h-full w-[3px] shrink-0"
            style={{ background: stripe }}
          />
        </span>
      ))}
    </span>
  );
};

// A tile with confetti above it, or the same tile sitting quietly.
const CelebratePreview = ({ value }) => (
  <span className="relative flex h-8 w-12 shrink-0 items-end justify-center overflow-hidden rounded-md border border-slate-200 bg-[#F5F7FA] px-1.5 pb-1 shadow-sm dark:border-white/10 dark:bg-[#0E121B]">
    {value
      ? [
          { c: '#38BDF8', l: '18%', t: '18%' },
          { c: '#8B5CF6', l: '44%', t: '8%' },
          { c: '#10B981', l: '68%', t: '22%' },
          { c: '#F59E0B', l: '82%', t: '10%' },
        ].map((p) => (
          <span
            key={p.l}
            className="absolute h-1 w-1 rounded-[1px]"
            style={{ background: p.c, left: p.l, top: p.t }}
          />
        ))
      : null}
    <span className="h-2 w-full rounded-sm border border-slate-200/80 bg-white dark:border-white/10 dark:!bg-[#222530]" />
  </span>
);

// One accordion section: clickable header (label + current value + chevron),
// options revealed below. Parent owns which section is open — one at a time.
export const SettingsSection = ({
  id,
  label,
  valueLabel,
  openSection,
  onToggle,
  options,
  value,
  onChange,
  saving,
  Preview,
}) => {
  const open = openSection === id;
  // Open, the row and its choices are one card (Nic 2026-10-20: make it
  // clear the options belong to the row that was clicked): a soft tinted
  // box around both (no guide line, Nic 2026-10-20).
  return (
    <div
      className={`mx-1.5 rounded-xl transition-colors ${
        open ? 'bg-slate-100/80 p-1 ring-1 ring-slate-200 dark:!bg-white/5 dark:ring-white/10' : ''
      }`}
    >
      <button
        type="button"
        onClick={() => onToggle(open ? null : id)}
        aria-expanded={open}
        className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-2 text-left outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 ${
          open ? '' : 'hover:bg-slate-50 dark:hover:bg-white/5'
        }`}
      >
        <span className="min-w-0 flex-1 text-xs font-semibold text-slate-700 dark:text-slate-200">
          {label}
        </span>
        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:!bg-white/10 dark:text-slate-300">
          {valueLabel}
        </span>
        <ChevronDown
          size={13}
          className={`shrink-0 text-slate-400 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div className="mb-1 space-y-1.5 px-1">
          {options.map((opt) => {
            const active = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={saving}
                onClick={() => onChange(opt.value)}
                className={`flex w-full items-center gap-2.5 rounded-lg bg-white p-2 text-left shadow-[0_1px_2px_rgba(15,23,42,0.05)] outline-none transition-colors focus:outline-none dark:!bg-surface-elevated ${
                  active
                    ? 'ring-1 ring-inset ring-emerald-400/70 dark:ring-emerald-500/50'
                    : 'hover:ring-1 hover:ring-inset hover:ring-slate-300 dark:hover:ring-white/20'
                }`}
              >
                <Preview value={opt.value} />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200">
                    {opt.label}
                  </span>
                  <span className="block truncate text-[11px] text-slate-400">
                    {opt.caption}
                  </span>
                </span>
                {active ? (
                  <Check size={14} className="shrink-0 text-emerald-500" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const BoardSettings = ({
  tileStyle,
  onChangeTileStyle,
  theme,
  onChangeTheme,
  celebrate,
  onChangeCelebrate,
  saving,
  bioMissing = false,
  canWork = false,
  onOpenCallProfile,
  onOpenTestAudio,
}) => {
  const [open, setOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const rootRef = useRef(null);

  // Test Audio is pointless (and guarded against) while any call is going -
  // hide the item rather than let it dead-end on the "finish the current
  // call first" toast. `active` covers a merely-open command center too,
  // matching startTestCall's own guard.
  const { active: liveCall, testCall } = useCallCenter();
  const callBusy =
    !!liveCall || testCall?.status === 'live' || testCall?.status === 'dialing';

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const labelFor = (opts, v) =>
    opts.find((o) => o.value === v)?.label || opts[0].label;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setOpenSection(null);
        }}
        title="Board display settings"
        className={`relative flex items-center rounded-full bg-white p-2 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] transition-colors dark:!bg-surface-card dark:ring-1 dark:ring-white/10 ${
          open
            ? 'text-slate-800 dark:text-slate-100'
            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
        }`}
      >
        <Settings2 size={14} />
        {bioMissing ? (
          <span
            title="Your bio isn't filled in yet"
            className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white ring-2 ring-white dark:ring-surface-page"
          >
            !
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-30 mt-2 w-64 rounded-xl bg-white p-2 shadow-[0_4px_12px_rgba(15,23,42,0.08),0_12px_32px_rgba(15,23,42,0.12)] dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10">
          <div className="flex items-center justify-between px-2.5 pb-1 pt-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Display
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
            >
              <X size={13} />
            </button>
          </div>
          <SettingsSection
            id="tileStyle"
            label="Tile style"
            valueLabel={labelFor(TILE_STYLE_OPTIONS, tileStyle)}
            openSection={openSection}
            onToggle={setOpenSection}
            options={TILE_STYLE_OPTIONS}
            value={tileStyle}
            onChange={onChangeTileStyle}
            saving={saving}
            Preview={TileStylePreview}
          />
          <SettingsSection
            id="theme"
            label="Theme"
            valueLabel={labelFor(THEME_OPTIONS, theme)}
            openSection={openSection}
            onToggle={setOpenSection}
            options={THEME_OPTIONS}
            value={theme}
            onChange={onChangeTheme}
            saving={saving}
            Preview={ThemePreview}
          />
          <SettingsSection
            id="celebrate"
            label="Celebrate arrivals"
            valueLabel={labelFor(CELEBRATE_OPTIONS, celebrate)}
            openSection={openSection}
            onToggle={setOpenSection}
            options={CELEBRATE_OPTIONS}
            value={celebrate}
            onChange={onChangeCelebrate}
            saving={saving}
            Preview={CelebratePreview}
          />
          {/* Your own tools: bio and test call. The manager tools (Schedule,
              Cadence editor, Call guide) live in Settings (Nic 2026-10-07). */}
          <div className="mx-1.5 my-1 h-px bg-slate-100 dark:!bg-white/10" />
          <div className="px-1.5 pb-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenCallProfile();
              }}
              className="flex w-full items-center gap-2 rounded-lg px-1.5 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5"
            >
              <CalendarClock size={13} className="text-slate-400" />
              My bio
              {bioMissing ? (
                <span className="ml-auto flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                  !
                </span>
              ) : null}
            </button>
            {/* Placing calls needs onboarding_board:work (the server gate on
                /test-call/check) - view-only users would only dead-end on a
                403, so they never see the item. Hidden during any call too. */}
            {canWork && !callBusy ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onOpenTestAudio();
                }}
                className="flex w-full items-center gap-2 rounded-lg px-1.5 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5"
              >
                <Headphones size={13} className="text-slate-400" />
                Test call audio
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// The Onboarding Support board. Layout is one-screen at every size, no
// horizontal scrolling: Intake + Contact Made are top lanes (the two
// highest-attention areas — the speed-to-take clock and the dwell clock);
// below, the 9 day columns share the full width as a fluid grid, with the
// quiet columns (Day 4, Days 8-9) collapsed to slim rails. Real-time via SSE
// hints (30s polling fallback) + a 15s local tick for countdowns.
// "Closed today (BAKER DAY). The board opens again Monday at 8:00 AM." lives
// in workspace/DeskKit.js (closedTodayLabel) since 2026-11-01: the Onboarding
// Workspace says the same thing.

const BoardPage = () => {
  const { user, hasPermission, hasRole } = useUser();
  const canManage = hasPermission('onboarding_board:manage');
  const canWork = hasPermission('onboarding_board:work');

  // Fixed behavior, no toggle: managers always see the whole board,
  // specialists always see their own lanes.
  const showAll = canManage;
  const board = useFetchBoard(showAll ? { all: true } : {});
  // Celebrate a new arrival. Read through a ref because this callback is
  // created before the pref is resolved below, and because a fresh identity
  // each render would tear down and reconnect the SSE stream.
  const celebrateRef = useRef(false);
  useBoardStream(true, (ev) => {
    // `lead_arrived` is the UNIVERSAL arrival signal: recordLeadEvent
    // publishes it for eventType 'received', which every creation path
    // records. `lead_received` looks like the better match but is published
    // only by assignIntakeLead — and createReferralLead never calls that, so
    // keying on it silently skipped every referral-form lead.
    //
    // Partner-API arrivals publish BOTH, so listening to this one alone is
    // also what keeps it to a single burst per lead (the throttle in
    // Celebrate.js is the backstop, not the mechanism).
    if (ev?.type === 'lead_arrived' && celebrateRef.current) {
      fireArrivalConfetti();
    }
  });

  // Display prefs: the server value (board payload) is the source of truth; a
  // local override makes the click feel instant while the PATCH lands.
  const [tileStyleLocal, setTileStyleLocal] = useState(null);
  const savePrefs = useSaveBoardPrefs();
  const tileStyle = tileStyleLocal || board.data?.prefs?.tileStyle || 'classic';
  const changeTileStyle = (value) => {
    setTileStyleLocal(value);
    savePrefs.mutate({ tileStyle: value });
  };

  // Theme (2026-08-06): same pattern. The flip must be INSTANT everywhere —
  // theme-snap kills every CSS transition for the flip frame (double rAF),
  // so no element animates its color change while the rest snap.
  // The server pref only arrives WITH the board payload — navigating in cold
  // would paint one light frame then fade dark (transition-colors). A local
  // echo of the last-known theme bridges that gap; the server value still
  // wins the moment it lands (a stale echo self-corrects in one frame).
  const [themeLocal, setThemeLocal] = useState(null);
  const cachedTheme = useMemo(() => {
    try {
      return localStorage.getItem('osBoardTheme');
    } catch {
      return null;
    }
  }, []);
  const theme =
    themeLocal || board.data?.prefs?.theme || cachedTheme || 'light';
  const changeTheme = (value) => {
    const root = document.documentElement;
    root.classList.add('theme-snap');
    setThemeLocal(value);
    savePrefs.mutate({ theme: value });
    requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.remove('theme-snap')),
    );
  };
  useEffect(() => {
    try {
      localStorage.setItem('osBoardTheme', theme);
    } catch {
      /* private mode etc. — the echo is only an optimization */
    }
  }, [theme]);

  // Confetti on arrival (Nic 2026-08-14). ABSENT pref = not chosen, so the
  // default is resolved from the viewer's role: Onboarding Support
  // Specialists live on this board all day and get it on; everyone else opts
  // in from the same menu. No localStorage echo like the theme has — that one
  // exists to stop a light frame painting before the server pref lands, and
  // there is nothing here to paint.
  const [celebrateLocal, setCelebrateLocal] = useState(null);
  const celebrate =
    celebrateLocal ??
    board.data?.prefs?.celebrateArrivals ??
    hasRole('onboarding_support_specialist');
  const changeCelebrate = (value) => {
    setCelebrateLocal(value);
    savePrefs.mutate({ celebrateArrivals: value });
  };
  // The stream callback is wired above (it has to be, the hook runs first),
  // so the live value reaches it through this ref.
  celebrateRef.current = celebrate;

  // Arrivals that landed while the tab was hidden get ONE burst on return
  // (not one per lead). Tied to the board's lifetime: navigate away and
  // anything owed is dropped, because this celebrates the board.
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && celebrateRef.current) flushPendingCelebration();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearPendingCelebration();
    };
  }, []);

  // Dark mode is scoped to THIS page but the class lives on <html> — the
  // outcome sheet, SLA tooltips, and dialer modal portal to document.body,
  // and this is the only ancestor they share with the board. Removed on
  // unmount/route-away, so the rest of the app never sees it. LAYOUT effect:
  // the class must be on <html> before the first paint or the rails flash
  // white and fade.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    return () => root.classList.remove('dark');
  }, [theme]);

  // Page flag for board-scoped global CSS (toast position/theming in
  // onboardingSupport.css) — toasts portal to <body>, so scoping has to
  // ride a class above them.
  useLayoutEffect(() => {
    document.documentElement.classList.add('os-board');
    return () => document.documentElement.classList.remove('os-board');
  }, []);

  // Local clock for countdowns; server data carries the timestamps.
  // 1s tick = live seconds countdowns and color flips at the true instant.
  // Cost is trivial: this is a purely LOCAL re-render (no network — data
  // freshness comes from SSE + the 30s poll), and the tile tree is light.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Selection survives refetches by id; content always renders live data.
  const [selected, setSelected] = useState(null); // {type:'intake'|'run', id}
  // A search result that is on someone else's board (Nic 2026-10-14).
  const [notYours, setNotYours] = useState(null);
  // Add a person (Nic 2026-10-20): { prefill } while the form is open.
  const [addPerson, setAddPerson] = useState(null);
  const [pendingRun, setPendingRun] = useState(null); // take response until refetch lands
  // Calls run through the app-level Call Command Center (2026-08-12): the
  // provider owns the dialer, the stepped in-call flow, SLA placement
  // stamping, transfers, and the post-call prompts. This page just hands it
  // the run + step + a board-context snapshot.
  const callCenter = useCallCenter();
  // Two views (Nic 2026-09-22): Making Contact (intake + the 10-day cadence)
  // and Potential Clients (contact made, not signed yet). Kept in the URL so
  // a refresh lands where you were.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = ['clients', 'nurture', 'dropped'].includes(searchParams.get('view'))
    ? searchParams.get('view')
    : 'contact';
  const setView = (next) => {
    const params = new URLSearchParams(searchParams);
    if (['clients', 'nurture', 'dropped'].includes(next)) params.set('view', next);
    else params.delete('view');
    setSearchParams(params, { replace: true });
  };
  // Made contact on a call (Nic 2026-10-10): they move to Signing Up, so the
  // board follows them there. The call center says so with a window event.
  // It fires only in the caller's own browser, so it also closes the side
  // panel the call was started from (Nic 2026-10-14): the person working the
  // lead gets the Workspace alone; the panel is for managers looking on.
  const setViewRef = useRef(setView);
  setViewRef.current = setView;
  useEffect(() => {
    const onContactMade = () => {
      setViewRef.current('clients');
      setSelected(null);
    };
    window.addEventListener('callcenter:contact-made', onContactMade);
    return () => window.removeEventListener('callcenter:contact-made', onContactMade);
  }, []);
  const [directSignupsOpen, setDirectSignupsOpen] = useState(false);
  // The Nurture tab's count, and whether anyone parked has reached out.
  const nurtureSummary = useNurtureSummary();
  // The Dropped tab's count, and whether anyone dropped reached back out
  // (Nic 2026-10-15).
  const droppedSummary = useDroppedSummary();
  // The board's ONE search (Nic 2026-09-28): the header box both finds
  // anyone (its dropdown jumps to them) and filters every tab's list. A tab
  // count with matches gets a blue ring, so a hit on another tab shows.
  const [findText, setFindText] = useState('');
  // Header layouts are picked at set widths, never left to wrapping (Nic
  // 2026-09-28: every in-between width looked accidental). 2xl: one line.
  // Below: title + icons, then a tools row. The color key from lg up.
  // A slot on the tabs line for the Signing Up tab's actions (Meetings).
  const [actionsSlot, setActionsSlot] = useState(null);
  const [findQ, setFindQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setFindQ(findText.trim()), 250);
    return () => clearTimeout(t);
  }, [findText]);
  const nurtureHits = useNurtureList(
    { page: 1, q: findQ },
    { enabled: !!findQ },
  );
  const droppedHits = useDroppedList(
    { page: 1, q: findQ },
    { enabled: !!findQ },
  );
  // The meetings calendar overlay (2026-09-24).
  const [meetingsOpen, setMeetingsOpen] = useState(false);
  // A Schedule meeting for someone no longer on the board.
  const [offBoardMeeting, setOffBoardMeeting] = useState(null);
  // The call workspace's talk track editor (managers).
  const [showCallGuide, setShowCallGuide] = useState(false);
  // The just-claimed direct signup's lead, ringed in the list for a moment.
  const [flashLeadId, setFlashLeadId] = useState(null);
  // A Nurture result picked in the people search: { leadId, n }.
  const [nurtureFocus, setNurtureFocus] = useState(null);
  // A Dropped result picked in the people search: { leadId, n }.
  const [droppedFocus, setDroppedFocus] = useState(null);
  // Only the open tab is on screen (Nic 2026-10-20, like Client Onboarding),
  // so Nurture's and Dropped's page and chips live here and survive a switch.
  const [nurtureKeep, setNurtureKeep] = useState({ page: 1, reason: '', mine: false, q: '' });
  const [droppedKeep, setDroppedKeep] = useState({ page: 1, reason: '', mine: false, q: '' });
  // A search highlight is for that visit only: leaving the tab drops it, so
  // coming back doesn't ring the row (and reset the chips) again.
  useEffect(() => {
    if (view !== 'nurture') setNurtureFocus(null);
    if (view !== 'dropped') setDroppedFocus(null);
  }, [view]);

  // ---- Tab motion (Nic 2026-10-20: the same as Client Onboarding) ----
  // A measured pill slides to the picked tab, and the picked view eases in
  // from the side it sits on. Only the open view is on the page: the old
  // four-wide sliding track kept every view rendered and loading, could be
  // left half off screen by a browser scroll, and let the keyboard wander
  // into the hidden ones.
  const tabListRef = useRef(null);
  const tabRefs = useRef({});
  const [pill, setPill] = useState(null); // { left, width, animate }
  const pillView = useRef(null);
  useLayoutEffect(() => {
    // Only a tab change glides the pill; a resize or a count change snaps it.
    const measure = () => {
      const el = tabRefs.current[view];
      if (!el) return;
      const animate = pillView.current !== null && pillView.current !== view;
      pillView.current = view;
      setPill((p) =>
        p && p.left === el.offsetLeft && p.width === el.offsetWidth
          ? p
          : { left: el.offsetLeft, width: el.offsetWidth, animate },
      );
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && tabListRef.current) ro.observe(tabListRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [view]);
  const viewBoxRef = useRef(null);
  const prevView = useRef(view);
  useLayoutEffect(() => {
    const from = prevView.current;
    prevView.current = view;
    const node = viewBoxRef.current;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!node || from === view || reduce) return undefined;
    const order = ['contact', 'clients', 'nurture', 'dropped'];
    const dir = order.indexOf(view) > order.indexOf(from) ? 1 : -1;
    node.style.transition = 'none';
    node.style.transform = `translateX(${dir * 28}px)`;
    node.style.opacity = '0';
    const raf = requestAnimationFrame(() => {
      node.style.transition =
        'transform 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease-out';
      node.style.transform = 'translateX(0)';
      node.style.opacity = '1';
    });
    // Drop the transform once settled: a lingering one makes a new
    // containing block for anything fixed inside the view.
    const done = setTimeout(() => {
      node.style.transition = '';
      node.style.transform = '';
      node.style.opacity = '';
    }, 300);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(done);
      node.style.transition = '';
      node.style.transform = '';
      node.style.opacity = '';
    };
  }, [view]);
  // Arriving from the call wrap-up's "Go there": ring that lead, then clean
  // the URL so a refresh doesn't ring it again.
  const focusLeadId = searchParams.get('focus');
  useEffect(() => {
    if (!focusLeadId) return;
    setFlashLeadId(focusLeadId);
    const params = new URLSearchParams(searchParams);
    params.delete('focus');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusLeadId]);
  useEffect(() => {
    if (!flashLeadId) return undefined;
    const t = setTimeout(() => setFlashLeadId(null), 4000);
    return () => clearTimeout(t);
  }, [flashLeadId]);
  const potential = useFetchPotentialClients();
  // Stage cadence actions (2026-09-24): the row's text step opens the
  // composer. No-shows are marked in Calendly only (Nic 2026-09-29): its
  // webhook moves the card, so our records never disagree with Calendly's.
  const [nudgeRun, setNudgeRun] = useState(null);
  // Quiet columns are slim by default; click to peek.
  const [expandedQuiet, setExpandedQuiet] = useState(() => new Set());
  // Manager schedule slideout.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Events bucket (2026-08-24): new event-partner leads, claimable into the
  // cadence like backlog leads.
  const [eventsOpen, setEventsOpen] = useState(false);
  // "My bio" modal (settings gear).
  const [showCallProfile, setShowCallProfile] = useState(false);
  const [showTestAudio, setShowTestAudio] = useState(false);
  // Coaching nag (Nic 2026-08-09): anyone who can WORK the board with no
  // bio saved gets a red mark on the gear + next to "My bio" until they
  // fill it in (sends that use [bio] are blocked without one).
  const bioProfile = useFetchCallProfile({ enabled: canWork });
  const bioMissing =
    canWork && !!bioProfile.data && !bioProfile.data.profile?.userCadenceBio;
  // The Cadence Editor is its own page since 2026-10-23 (Admin > Cadence
  // Editor, /admin/cadence-editor); the board no longer opens it.
  // "Check back in" (Nic 2026-08-10): end today's lunch early; the next
  // board payload carries the capped lunch end, clearing banner/pill.
  // Away shows the moment it is clicked (Nic 2026-10-05: waiting on the
  // board reload felt slow). localAway = a break we just started; backAt =
  // the "I'm back" click. Neither is cleared when a reload lands: a poll
  // already in flight at the click can land first with the OLD data and
  // flash the banner back (Nic saw it). backAt is safe to keep: it only
  // hides pauses that began before the click, and the server ended those.
  // localAway steps aside once the server's own break shows up, and simply
  // expires at its end time.
  const [localAway, setLocalAway] = useState(null);
  const [backAt, setBackAt] = useState(null);
  const lunchCheckInMut = useLunchCheckIn(() => board.refetch());
  const goAwayMut = useGoAway((res) => {
    const a = res?.away;
    if (a) {
      setLocalAway({
        kind: 'away',
        startAt: a.startedAt,
        endAt: a.endsAt,
        reason: a.reason,
        note: a.note || null,
      });
    }
    board.refetch();
  });
  const stepAway = (pick) => {
    setBackAt(null);
    setLocalAway({
      kind: 'away',
      startAt: new Date().toISOString(),
      endAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      reason: pick.reason,
      note: pick.note || null,
    });
    goAwayMut.mutate(pick, {
      onError: (err) => {
        setLocalAway(null);
        toast.error(err?.response?.data?.msg || "Couldn't set you away.");
      },
    });
  };
  const imBack = () => {
    setBackAt(Date.now());
    setLocalAway(null);
    lunchCheckInMut.mutate(undefined, {
      onError: () => setBackAt(null),
    });
  };
  // Today's schedule with the viewer's own clicks applied (Nic 2026-10-20:
  // the tiles lagged after Step away): a break just started joins their
  // pauses, and "I'm back" ends any pause that began before it, until the
  // board reload carries the server's copy. Tiles and the side panel read
  // this, so their clocks freeze and unfreeze the moment you click.
  const scheduleNow = (() => {
    const list = board.data?.todaySchedule || [];
    const me = board.data?.viewerUserId;
    if (!me || (!localAway && !backAt)) return list;
    return list.map((p) => {
      if (p.userId !== me) return p;
      let pauses = p.meetingPauses || [];
      // "I'm back" never ends a call (2026-11-02): the call does.
      if (backAt) {
        pauses = pauses.map((x) =>
          x.kind !== 'call' &&
          new Date(x.startAt).getTime() <= backAt &&
          pauseEndMs(x) > backAt
            ? { ...x, endAt: new Date(backAt).toISOString(), open: false }
            : x,
        );
      }
      if (
        localAway &&
        !pauses.some(
          (x) =>
            x.kind === 'away' &&
            Math.abs(new Date(x.startAt) - new Date(localAway.startAt)) < 60000,
        )
      ) {
        pauses = [...pauses, localAway];
      }
      return { ...p, meetingPauses: pauses };
    });
  })();

  // The viewer's pauses right now, with the clicks above applied.
  const myPausesNow = () => {
    const me = board.data?.viewerUserId;
    const mine = (board.data?.todaySchedule || []).find((p) => p.userId === me);
    const t = now.getTime();
    const live = (p) =>
      pauseLiveAt(p, t) &&
      !(backAt && p.kind !== 'call' && new Date(p.startAt).getTime() <= backAt);
    const server = (
      mine?.meetingPauses ||
      board.data?.ownerMeetingPauses?.[me] ||
      []
    ).filter(live);
    // The just-clicked break, until the server's own copy arrives.
    const list =
      localAway && live(localAway) && !server.some((p) => p.kind === 'away')
        ? [...server, localAway]
        : server;
    // Lunch only for people on today's schedule (the LunchPill rule: the org
    // default is nobody's own lunch).
    const lunch = mine
      ? { startAt: mine.lunchStartAt, endAt: mine.lunchEndAt }
      : null;
    return { list, lunch: live(lunch) ? lunch : null };
  };

  // Manager spotlight (Nic 2026-08-11): click an avatar circle to hide that
  // specialist's tiles and focus on the rest. Session-local, display-only —
  // nothing about ownership or clocks changes. Assigned intake leads follow
  // their assignee's toggle; communal (unassigned) leads always show.
  const [hiddenOwners, setHiddenOwners] = useState(() => new Set());
  const toggleOwnerHidden = (userId) =>
    setHiddenOwners((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const intake = useMemo(() => {
    const list = (board.data?.intake || []).filter(
      (l) =>
        (!l.intakeAssignedTo || !hiddenOwners.has(l.intakeAssignedTo)) &&
        leadMatchesQuery(l, findQ),
    );
    // Longest-waiting first — the lane reads left-to-right by urgency.
    return [...list].sort(
      (a, b) => new Date(a.leadCreatedOn) - new Date(b.leadCreatedOn),
    );
  }, [board.data, hiddenOwners, findQ]);
  // A new lead's clock stands still for ANY time its owner is away (Nic
  // 2026-10-21: lunch, a meeting, a break), the same pauses a taken tile
  // uses. A communal one has nobody to be away (Nic 2026-10-21), so it never
  // pauses; the rotation gives every arrival an owner anyway.
  const intakePausesFor = (lead) => {
    if (!lead.intakeAssignedTo) return null;
    // scheduleNow, like the run tiles: a Step away click freezes the tile at
    // once, not at the next board load (branch QA 2026-10-21).
    const entry = scheduleNow.find(
      (p) => p.userId === lead.intakeAssignedTo,
    );
    return entry
      ? ownerPausesOf(entry)
      : fallbackPausesOf(
          board.data?.defaultLunch,
          board.data?.ownerMeetingPauses?.[lead.intakeAssignedTo],
        );
  };
  const runs = useMemo(() => board.data?.runs || [], [board.data]);
  const cadenceVersion = board.data?.cadenceVersion || null;
  // The org work calendar (2026-08-15). Drives when a step closes, whether
  // countdowns run at all, and the closed-day banner. Null until the payload
  // lands, which every helper treats as "no calendar" and degrades gracefully.
  const workCalendar = board.data?.workCalendar || null;
  // A closed day (weekend, day off): the Signing Up words say the real day.
  const closedUntil = workCalendar && workCalendar.isWorkingToday === false ? workCalendar.nextOpenAt : null;

  const runById = useMemo(
    () => new Map(runs.map((r) => [r.cadenceRunId, r])),
    [runs],
  );
  // A call picked up from the RingCentral app (2026-11-02): the provider
  // switched the audio in and sent us here with ?pickup=<run>. Once the run
  // is in this board's data, open its Workspace already live, then clean
  // the URL. The Workspace needs this board's context (transfer targets,
  // call guide, scripts), which is why the provider cannot open it alone.
  const pickupRunId = searchParams.get('pickup');
  const pendingPickup = callCenter.pendingPickup || null;
  useEffect(() => {
    if (!pickupRunId || !pendingPickup) return;
    if (String(pendingPickup.cadenceRunId) !== String(pickupRunId)) return;
    const run = runById.get(pickupRunId);
    if (!run) return;
    // The provider clears the card once the start takes; a refused start
    // leaves the card to fall back to its on-the-line view.
    const adopted = pendingPickup;
    if (run.state === 'contact_made') {
      callCenter.startFollowUpCall({ run, boardContext: meetingBoardContext(), mode: 'guide', adopted });
    } else {
      callCenter.startOutsideCadenceCall({ run, boardContext: meetingBoardContext(), adopted });
    }
    const params = new URLSearchParams(searchParams);
    params.delete('pickup');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupRunId, pendingPickup, runById]);
  // Manager spotlight applies to the Workload forecast too (Nic
  // 2026-08-24): untick a specialist's circle and their scheduled steps
  // leave the counts, same as their tiles leave the board.
  // Workload is hidden for now (see the header); kept for a quick return.
  // eslint-disable-next-line no-unused-vars
  const workloadRuns = useMemo(
    () => runs.filter((r) => !hiddenOwners.has(r.ownerUserId)),
    [runs, hiddenOwners],
  );
  // From the meetings calendar or a meeting alert: open the lead's panel, or
  // start the call the same way the panel's Call now does. Only leads with a
  // live board run can do either.
  // Who may act (Nic 2026-10-23), with their id so a re-engage toast can
  // tell "your Signing Up" from someone else's.
  const viewerForLists = board.data?.viewer
    ? { ...board.data.viewer, userId: board.data?.viewerUserId }
    : null;
  const meetingBoardContext = () => ({
    transferTargets: board.data?.transferTargets || [],
    salesReps: board.data?.salesReps || [],
    viewerUserId: board.data?.viewerUserId,
    // Who may act, and the manager (Nic 2026-10-23).
    viewer: board.data?.viewer || null,
    orgCalendlyUrl: board.data?.orgCalendlyUrl || '',
    callGuide: board.data?.callGuide || null,
    scripts: board.data?.scripts || {},
  });
  // One place to work a person (Nic 2026-10-06): a Signing Up card opens
  // the Workspace, not dialed yet, on the Sign up tab once the signup is
  // underway. Someone else's card opens the same Workspace read only (Nic
  // 2026-10-23: the manager, admins and viewers see the Workspace, never
  // the side panel; only Making Contact keeps that). The owner's own lead
  // with no phone still opens the side panel (nothing to dial).
  const SIGNUP_UNDERWAY = ['signup_link_sent', 'account_made', 'agreement_out'];
  const openSigningUpRun = (run, { tab = null } = {}) => {
    const mine =
      canWork &&
      board.data?.viewer?.canAct !== false &&
      isOwnedBy(run?.ownerUserId, board.data?.viewerUserId);
    if (mine && !run?.lead?.leadPhone) {
      setSelected({ type: 'run', id: run.cadenceRunId });
      return;
    }
    callCenter.startFollowUpCall({
      run,
      boardContext: meetingBoardContext(),
      // A tab asked for (Notes) wins over the signup tab.
      mode: !tab && SIGNUP_UNDERWAY.includes(run.pipelineStage) ? 'signup' : 'guide',
      tab,
      readOnly: !mine,
    });
  };
  // A people search result (and the Add a person form's matches) opens
  // where that person is. A run opens its panel; Intake opens the intake
  // panel; Nurture has no panel, so it shows the (filtered) list. A Signing
  // Up result opens the Workspace, the same as its card (Nic 2026-10-06).
  const openSearchResult = (r) => {
    const live = r.cadenceRunId ? runById.get(r.cadenceRunId) : null;
    // Someone else's lead the viewer can't see on their board (Nic
    // 2026-10-14): say whose it is and who can move it, instead of
    // switching tabs to nothing.
    if (
      r.cadenceRunId &&
      !live &&
      r.ownerUserId &&
      !isOwnedBy(r.ownerUserId, board.data?.viewerUserId)
    ) {
      setNotYours(r);
      return;
    }
    if (live && r.onBoardView === 'clients') {
      setView('clients');
      openSigningUpRun(live);
    } else if (r.cadenceRunId) {
      setView(r.onBoardView || 'contact');
      setSelected({ type: 'run', id: r.cadenceRunId });
    } else if (r.standing?.status === 'intake') {
      setView('contact');
      setSelected({ type: 'intake', id: r.leadId });
    } else if (r.standing?.status === 'dropped') {
      // Same for Dropped (2026-10-15).
      setView('dropped');
      setDroppedFocus({ leadId: r.leadId, n: Date.now() });
    } else {
      // Clear the Nurture chips and ring their row, so a filter can't hide
      // them (Nic 2026-10-08).
      setView('nurture');
      setNurtureFocus({ leadId: r.leadId, n: Date.now() });
    }
  };
  // The form's matches speak lookup, not search: map them over.
  const openPersonMatch = (m) => {
    setAddPerson(null);
    // The search box then holds who it found, so the old search can't hide
    // them on the tab it opens (Nic 2026-10-21: typed "Mo Low", the form
    // found Mia by phone, Go to Dropped showed nothing).
    setFindText(String(m.name || '').trim());
    const [firstName, ...rest] = String(m.name || '').split(/\s+/);
    openSearchResult({
      ...m,
      firstName,
      lastName: rest.join(' '),
      standing: {
        status:
          m.action === 'show_dropped' ? 'dropped' : m.action === 'show_nurture' ? 'nurture' : m.scenario,
      },
    });
  };
  const openMeetingLead = (m) => {
    const run = m?.cadenceRunId ? runById.get(m.cadenceRunId) : null;
    if (!run) {
      // Off the board (Nic 2026-10-26): the meeting itself, with what can
      // still be done to it, in place of the old toast.
      setOffBoardMeeting(m);
      return;
    }
    setMeetingsOpen(false);
    // Signing Up opens the Workspace, the same as its card (2026-10-06).
    if (run.state === 'contact_made') {
      setView('clients');
      openSigningUpRun(run);
      return;
    }
    setSelected({ type: 'run', id: run.cadenceRunId });
  };
  // Ownership rule (Nic 2026-09-29): Call now only on the viewer's own
  // leads. The calendar hides it otherwise; alerts land here and refuse.
  const meetingIsMine = (m) => {
    const run = m?.cadenceRunId ? runById.get(m.cadenceRunId) : null;
    return !!run && isOwnedBy(run.ownerUserId, board.data?.viewerUserId);
  };
  const callMeetingLead = (m) => {
    const run = m?.cadenceRunId ? runById.get(m.cadenceRunId) : null;
    if (!run) {
      toast.info("That lead isn't on the board right now.");
      return;
    }
    if (!isOwnedBy(run.ownerUserId, board.data?.viewerUserId)) {
      toast.error(notYoursMessage(run.ownerName));
      return;
    }
    setMeetingsOpen(false);
    const boardContext = meetingBoardContext();
    if (run.state === 'contact_made') {
      callCenter.startFollowUpCall({ run, boardContext });
    } else {
      callCenter.startOutsideCadenceCall({ run, boardContext });
    }
  };
  const meetingSummary = useMeetingAlerts({
    enabled: canWork,
    now,
    onCall: callMeetingLead,
    onOpen: openMeetingLead,
  });

  const contactRuns = useMemo(
    () =>
      runs
        .filter((r) => r.state === 'contact_made')
        .filter((r) => !hiddenOwners.has(r.ownerUserId))
        .filter((r) => leadMatchesQuery(r.lead, findQ))
        // Order by time IN THE COLUMN (the click instant), matching the
        // dwell clock — a backdated confirm must not sort as "oldest"
        // while wearing a fresh clock.
        .sort(
          (a, b) =>
            new Date(a.contactMadeRecordedAt || a.contactMadeAt) -
            new Date(b.contactMadeRecordedAt || b.contactMadeAt),
        ),
    [runs, hiddenOwners, findQ],
  );
  // Hide-done toggle (Ownership round): drop the green "Done for today"
  // tiles. Safe left on — done-for-today recomputes each day, so a new day's
  // tiles always start visible.
  const [hideDoneLocal, setHideDoneLocal] = useState(null);
  const hideDoneToday =
    hideDoneLocal ?? board.data?.prefs?.hideDoneToday ?? false;
  const toggleHideDone = () => {
    const next = !hideDoneToday;
    setHideDoneLocal(next);
    savePrefs.mutate({ hideDoneToday: next });
  };

  // How many tiles Hide done is keeping off the columns right now (the eye's
  // number): the same test the columns apply, before the search narrows.
  const hiddenDoneCount = useMemo(
    () =>
      hideDoneToday
        ? runs.filter(
            (run) =>
              run.state === 'active' &&
              !hiddenOwners.has(run.ownerUserId) &&
              runDoneForToday(run, workCalendar, now),
          ).length
        : 0,
    [runs, now, workCalendar, hideDoneToday, hiddenOwners],
  );

  const runsByColumn = useMemo(() => {
    const map = new Map(DAY_COLUMNS.map((c) => [c.key, []]));
    runs.forEach((run) => {
      if (run.state !== 'active') return;
      if (hideDoneToday && runDoneForToday(run, workCalendar, now)) return;
      if (hiddenOwners.has(run.ownerUserId)) return;
      if (!leadMatchesQuery(run.lead, findQ)) return;
      const key = columnKeyForRun(run);
      if (map.has(key)) map.get(key).push(run);
    });
    // Attention sort: the top of every column is its hottest tile
    // (behind > act now > upcoming > quiet). Re-ranks on the 15s tick.
    map.forEach((list, key) =>
      map.set(key, sortByAttention(list, now, cadenceVersion, workCalendar)),
    );
    return map;
  }, [runs, now, cadenceVersion, workCalendar, hideDoneToday, hiddenOwners, findQ]);

  // Per-column reach-out counts for the header marker (Nic 2026-08-20).
  // Deliberately NOT derived from runsByColumn: that map has already dropped
  // done-today runs under Hide Done, and the marker's whole job is to say
  // "there's a raised hand on this day, go find it" when the tile itself is
  // hidden or scrolled out of view. Spotlight-hidden owners DO stay excluded
  // — their tiles aren't on the board at all, so a marker pointing at one
  // would send you looking for something that cannot be found.
  const reachOutsByColumn = useMemo(() => {
    const map = new Map(DAY_COLUMNS.map((c) => [c.key, 0]));
    runs.forEach((run) => {
      if (run.state !== 'active') return;
      if (hiddenOwners.has(run.ownerUserId)) return;
      if (!(run.reachOuts || []).length) return;
      const key = columnKeyForRun(run);
      if (map.has(key)) map.set(key, map.get(key) + 1);
    });
    return map;
  }, [runs, hiddenOwners]);

  // View-switch badges (Nic 2026-09-22): ACTIONABLE items only, so a number
  // on the view you are NOT looking at always means "go look".
  //   Making Contact: intake waiting to be taken + runs with a step due now
  //   or behind (the same rule as the browser-tab title below).
  //   Potential Clients: the Needs you today count.
  //   Only YOUR things (Nic 2026-10-23): a number means you have something
  //   to do, so it counts the viewer's own leads (plus communal ones they
  //   could take), even for the manager who sees everyone. An admin or a
  //   viewer, who works nothing, sees no numbers and no pulses at all.
  const tabViewerId = board.data?.viewerUserId;
  const tabsCanAct = board.data?.viewer?.canAct !== false;
  // Their to-dos (the same cache the desk uses), so a tick drops the tab too.
  const makingContactDue = useMemo(
    () =>
      !tabsCanAct
        ? 0
        : intake.filter((l) => !l.intakeAssignedTo || isOwnedBy(l.intakeAssignedTo, tabViewerId)).length +
          runs.filter(
            (r) =>
              r.state === 'active' &&
              isOwnedBy(r.ownerUserId, tabViewerId) &&
              attentionRank(r, now, cadenceVersion, workCalendar) <= 1,
          ).length,
    [intake, runs, now, cadenceVersion, workCalendar, tabsCanAct, tabViewerId],
  );
  // One number on the tab (Nic 2026-10-06): the red, act-today cards,
  // Details in included. The Details in chip on the card is the callout.
  // (Nic 2026-10-23) The same people as the desk's Your move today: their
  // own leads with a suggested to-do still open, so ticking one off drops
  // the tab too. Plus one for waiting direct signups.
  const potentialDue = !tabsCanAct
    ? 0
    : leadsNeedingYou(
        deskRowsOf(
          contactRuns,
          now,
          new Set((potential.data?.detailsReady || []).map((d) => d.cadenceRunId)),
          closedUntil,
        ),
        tabViewerId,
        now,
      ).size + ((potential.data?.directSignupCount || 0) > 0 ? 1 : 0);

  // While the search narrows the board, each tab says how many it shows of
  // how many it has (Nic 2026-10-21), so a quiet tab never reads as caught
  // up. Same owner and Hide Done filters as the lists; only the search differs.
  const searchCounts = useMemo(() => {
    if (!findQ) return null;
    const intakeAll = (board.data?.intake || []).filter(
      (l) => !l.intakeAssignedTo || !hiddenOwners.has(l.intakeAssignedTo),
    ).length;
    const activeAll = runs.filter(
      (r) =>
        r.state === 'active' &&
        !hiddenOwners.has(r.ownerUserId) &&
        !(hideDoneToday && runDoneForToday(r, workCalendar, now)) &&
        DAY_COLUMNS.some((c) => c.key === columnKeyForRun(r)),
    ).length;
    const activeShown = [...runsByColumn.values()].reduce((n, l) => n + l.length, 0);
    const signingAll = runs.filter(
      (r) => r.state === 'contact_made' && !hiddenOwners.has(r.ownerUserId),
    ).length;
    return {
      contact: { shown: intake.length + activeShown, total: intakeAll + activeAll },
      clients: { shown: contactRuns.length, total: signingAll },
      nurture: { shown: nurtureHits.data?.total || 0, total: nurtureSummary.data?.total || 0 },
      dropped: { shown: droppedHits.data?.total || 0, total: droppedSummary.data?.total || 0 },
    };
  }, [findQ, board.data, runs, intake, runsByColumn, contactRuns, hiddenOwners, hideDoneToday, workCalendar, now, nurtureHits.data, droppedHits.data, nurtureSummary.data, droppedSummary.data]);

  // Tab badge: the board screams even when the specialist is off in
  // RingCentral. Due = every intake lead (the take clock is always live)
  // + runs whose current step is in-window or behind.
  // The same as the Making Contact tab (Nic 2026-10-23): only your own.
  useEffect(() => {
    const dueCount = makingContactDue;
    const title =
      dueCount > 0
        ? `(${dueCount} due) Sales`
        : 'Sales';
    // Write only on change: this effect re-runs every clock tick, and a
    // cleanup here made the tab flash between the site title and the
    // board's every second (Nic 2026-08-19, backlog #70).
    if (document.title !== title) document.title = title;
  }, [makingContactDue]);
  // Restore the site title ONLY when actually leaving the board.
  useEffect(
    () => () => {
      document.title = 'Client Portal | Veritas Ratings';
    },
    [],
  );

  const selectedIntakeLead =
    selected?.type === 'intake'
      ? intake.find((l) => l.leadId === selected.id)
      : null;
  let selectedRun = selected?.type === 'run' ? runById.get(selected.id) : null;
  if (
    selected?.type === 'run' &&
    !selectedRun &&
    pendingRun?.cadenceRunId === selected.id
  ) {
    selectedRun = pendingRun;
  }
  useEffect(() => {
    if (pendingRun && runById.has(pendingRun.cadenceRunId)) setPendingRun(null);
  }, [pendingRun, runById]);

  const onMutationError = (err) =>
    toast.error(err?.response?.data?.msg || 'That didn’t go through.');

  // A send blocked by an unfilled [bio] opens the bio editor right away —
  // the fix is one field, so hand it to the specialist on the spot.
  const onSendError = (err) => {
    onMutationError(err);
    if (err?.response?.data?.code === 'unresolved_token') {
      setShowCallProfile(true);
    }
  };

  const takeLead = useTakeLead(null, {
    onError: (err) => {
      // Losing the race is normal on a busy board — refresh and move on.
      onMutationError(err);
      board.refetch();
      setSelected(null);
    },
  });
  const completeStep = useCompleteStep(null, { onError: onMutationError });
  const sendStepSms = useSendStepSms(null, { onError: onSendError });
  const sendStepEmail = useSendStepEmail(null, { onError: onSendError });
  const ackDetections = useAckDetections(null, { onError: onMutationError });
  const markContactMade = useMarkContactMade(null, {
    onError: onMutationError,
  });
  const setRunOutcome = useSetRunOutcome(null, { onError: onMutationError });
  // Drops must fail LOUDLY — this ran silent (no onError) until 2026-08-18.
  const deleteLead = useDeleteLead(null, { onError: onMutationError });
  const dismissStop = useDismissStop(null, { onError: onMutationError });
  const pushLead = usePushLead(null, { onError: onMutationError });
  const unpushLead = useUnpushLead(null, { onError: onMutationError });
  const startRunNow = useStartRunNow(null, { onError: onMutationError });
  // Manager reassignment (the slideout's schedule query doubles as the
  // roster source — shared cache key, one fetch). Targets are ONLY
  // Onboarding Support Specialists: reassigned leads must land on a board
  // whose owner returns to it (server enforces this too).
  const scheduleData = useFetchBoardSchedule({}, { enabled: canManage });
  // The reps plus the manager (Nic 2026-10-07: Jorge can take one).
  const reassignSpecialists = canManage
    ? scheduleData.data?.owners || scheduleData.data?.specialists || []
    : null;
  const afterReassign = () => {
    board.refetch();
    setSelected(null);
  };
  const reassignRun = useReassignRun(afterReassign, {
    onError: onMutationError,
  });
  const reassignIntake = useReassignIntake(afterReassign, {
    onError: onMutationError,
  });
  const busy =
    takeLead.isLoading ||
    completeStep.isLoading ||
    markContactMade.isLoading ||
    setRunOutcome.isLoading ||
    deleteLead.isLoading ||
    dismissStop.isLoading ||
    startRunNow.isLoading;

  const handleTake = async (lead) => {
    try {
      const data = await takeLead.mutateAsync({ leadId: lead.leadId });
      // Panel stays open and flips to the full cockpit.
      setPendingRun({
        ...data.run,
        lead,
        steps: data.steps,
        detections: { calls: 0, texts: 0 },
      });
      setSelected({ type: 'run', id: data.run.cadenceRunId });
      board.refetch();
    } catch (err) {
      /* handled in onError */
    }
  };

  const handleManualComplete = async (step) => {
    await completeStep.mutateAsync({ cadenceStepId: step.cadenceStepId });
    board.refetch();
  };

  // Phase 3 real sends. Errors are toasted by the mutation and rethrown so
  // the compose panel stays open with the draft intact.
  const handleSendSms = async (step, { text, rcAccessToken }) => {
    await sendStepSms.mutateAsync({
      cadenceStepId: step.cadenceStepId,
      text,
      rcAccessToken,
    });
    board.refetch();
  };

  const handleSendEmail = async (step, { subject, text }) => {
    await sendStepEmail.mutateAsync({
      cadenceStepId: step.cadenceStepId,
      subject,
      text,
    });
    board.refetch();
  };

  // Non-call contact (via picker, detections): the run-level stamp. Call
  // contact flows through the Call Command Center instead.
  const handleContactMade = async (via, leadOutreachId = null) => {
    const runId = selectedRun?.cadenceRunId;
    if (!runId) return;
    await markContactMade.mutateAsync({
      cadenceRunId: runId,
      via,
      ...(leadOutreachId ? { leadOutreachId } : {}),
    });
    // Same as Made contact on a call (Nic 2026-10-15): they move to Signing
    // Up, so the board follows them there and the Workspace opens; the side
    // panel closes (Signing Up works in the Workspace, not the panel).
    const { data } = await board.refetch();
    const moved = (data?.runs || []).find((r) => r.cadenceRunId === runId);
    if (moved?.state === 'contact_made') {
      setSelected(null);
      setView('clients');
      openSigningUpRun(moved);
    }
  };

  const handleOutcome = async (outcome, reason = null) => {
    if (!selectedRun) return;
    await setRunOutcome.mutateAsync({
      cadenceRunId: selectedRun.cadenceRunId,
      outcome,
      // Nurture carries its canned dropdown reason (Nic 2026-08-13).
      ...(reason ? { reason } : {}),
    });
    setSelected(null);
    board.refetch();
    toast.success(
      outcome === 'booked'
        ? 'Marked booked. Nice work.'
        : outcome === 'link_sent'
          ? 'Moved to Booking link sent. They stay on Signing Up until they book.'
          : 'Moved to Long Term Nurture.',
    );
  };

  // Drop from the board (Nic 2026-08-14) — the same DELETE /leads/:leadId the
  // Leads page and Lead Handoffs use, so the drop reason and partner-facing
  // semantics are identical. The server closes the cadence run in the same
  // transaction, so the lead never leaves an orphaned run behind.
  const handleDrop = async (reason) => {
    // `selected` is only ever {type, id} — the lead itself lives on the
    // derived run or intake selection (this read being wrong is what made
    // the Drop button a silent no-op on 8/14).
    const lead = selectedRun?.lead || selectedIntakeLead;
    if (!lead?.leadId || !reason) return;
    try {
      await deleteLead.mutateAsync({ leadId: lead.leadId, reason });
    } catch {
      return; // onError already toasted; keep the panel open to retry
    }
    setSelected(null);
    board.refetch();
    toast.success('Lead dropped.');
  };

  // "Keep working" a STOP-parked lead: clears the «STOP» chip only; the
  // panel stays open on the (still Contact Made) lead.
  const handleDismissStop = async (run) => {
    if (!run?.cadenceRunId) return;
    try {
      await dismissStop.mutateAsync({ cadenceRunId: run.cadenceRunId });
    } catch {
      return; // onError already toasted
    }
    board.refetch();
    toast.success('Kept. Texts stay suppressed; calls and email are open.');
  };

  // Reversible Push (Nic 2026-08-15): last-hour only (server-guarded). The
  // panel stays open; if reassignment moves the lead off this viewer's
  // board, the refetch clears it naturally.
  const handlePushLead = async (leadId) => {
    const data = await pushLead.mutateAsync({ leadId });
    board.refetch();
    toast.success(
      data?.body?.label
        ? `Pushed. Starts ${data.body.label}.`
        : 'Pushed to the next working day.',
    );
  };
  // Start now (Nic 2026-10-25): a waiting new lead, started early.
  const handleStartNow = async (run) => {
    if (!run?.cadenceRunId) return;
    try {
      await startRunNow.mutateAsync({ cadenceRunId: run.cadenceRunId });
    } catch {
      return; // onError already toasted
    }
    board.refetch();
    toast.success('Started. Its 10-day clock starts now.');
  };
  const handleUnpushLead = async (leadId) => {
    await unpushLead.mutateAsync({ leadId });
    board.refetch();
    // The server restarts its clock now, back with whoever pushed it (QA
    // 2026-10-26).
    toast.success('Push undone. Its 10-day clock starts now.');
  };

  const toggleQuiet = (key) =>
    setExpandedQuiet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <PrivateLayout
      background={theme === 'dark' ? '#0E121B' : '#F5F7FA'}
      hideFooter
      fluid
    >
      <BoardContexts tileStyle={tileStyle} workCalendar={workCalendar}>
        <div className="flex h-[calc(100vh-125px)] min-h-0 flex-col">
          {/* The top, like the Onboarding Workspace's (Nic 2026-10-23): the
              title on the left, the people and tools on the right, a thin
              rule, then the tabs and search. No greeting and no time bar
              (Nic: distracting). */}
          {(() => {
            const tools = (
              <>
              <AvatarStrip
                // Only for people who see everyone's cards (Nic 2026-10-07):
                // a rep sees just their own, so the team's circles say
                // nothing they can act on.
                todaySchedule={
                  canManage || board.data?.viewOnly
                    ? board.data?.todaySchedule || []
                    : []
                }
                now={now}
                // Spotlight toggling is a MANAGER tool — specialists' own
                // boards are already scoped to them.
                hiddenOwners={canManage ? hiddenOwners : null}
                onToggleOwner={canManage ? toggleOwnerHidden : null}
              />
              {/* Step away is for the reps who work the items, never admins
                  or the manager (Nic 2026-10-07). */}
              {/* The scoreboard chip (Nic 2026-11-03): the rep's own signups
                  today, with NEW until they open the list. Reps only, the
                  same check as Step away: for an admin, a manager or view
                  only it would always read 0. */}
              {board.data?.viewerIsSalesRep ? <ScoreboardChip team="sales" /> : null}
              {canWork && board.data?.viewerIsSalesRep ? (
                <StepAwayControl
                  busy={goAwayMut.isLoading}
                  onAway={stepAway}
                  away={
                    myPausesNow().list.some((p) => p.kind === 'away') ||
                    !!myPausesNow().lunch
                  }
                  onCall={myPausesNow().list.some((p) => p.kind === 'call')}
                />
              ) : null}
              {/* RingCentral and Calendly in one place (Nic 2026-10-20):
                  the setup that has to be green. Hide done moved into the
                  Display menu. Only for the team and its manager (Nic
                  2026-11-02, the same role check as acting on people): an
                  admin or viewer has no texts or calls to make here, and
                  the chip's number lookup would be refused anyway. */}
              {board.data?.viewer?.canAct === true ? (
                <ConnectionsControl
                  myCalendlyUrl={board.data?.myCalendlyUrl || ''}
                  // Calendly is gone (in-app meetings, 2026-10-25): RingCentral only.
                  withCalendly={false}
                />
              ) : null}
              {/* Events bucket (re-homed from Lead Handoffs 2026-08-24,
                  Nic): NEW event-partner leads wait here and get CLAIMED
                  into the cadence like backlog leads. */}
              {(board.data?.eventLeadCount || 0) > 0 ? (
                <button
                  type="button"
                  onClick={() => setEventsOpen(true)}
                  title="Events: claim to add to Day 1 in Making Contact"
                  className="flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[11px] font-medium text-slate-500 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] transition-colors hover:text-slate-700 dark:!bg-surface-card dark:text-slate-400 dark:ring-1 dark:ring-white/10 dark:hover:text-slate-200"
                >
                  <Tent size={13} />
                  <span className="os-act-label hidden sm:inline">Events</span>
                  <span className="rounded-full bg-slate-900 px-1.5 py-px text-[10px] font-semibold text-white dark:bg-slate-200 dark:!text-slate-900">
                    {board.data.eventLeadCount}
                  </span>
                </button>
              ) : null}
              </>
            );
            const icons = (
              <div className="flex shrink-0 items-center gap-2">
              <EagleEyeButton />
              {/* One Settings menu for managers and admins (Nic 2026-10-07):
                  the team settings plus this board's manager tools. Two
                  separate grants feed it (migration 171): board settings
                  and the cadence editor. */}
              {canManage ? (
                <TeamSettingsButton
                  team="sales"
                  tools={[
                    canManage && {
                      key: 'schedule',
                      label: 'Schedule',
                      detail: 'Hours, lunches and days off',
                      icon: CalendarCog,
                      onClick: () => setScheduleOpen(true),
                    },
                    canManage && {
                      key: 'guide',
                      label: 'Call guide',
                      detail: 'Talk track and FAQ for the Workspace',
                      icon: BookOpen,
                      onClick: () => setShowCallGuide(true),
                    },
                  ].filter(Boolean)}
                />
              ) : null}
              <BoardSettings
                tileStyle={tileStyle}
                onChangeTileStyle={changeTileStyle}
                theme={theme}
                onChangeTheme={changeTheme}
                celebrate={celebrate}
                onChangeCelebrate={changeCelebrate}
                saving={savePrefs.isLoading}
                canWork={canWork}
                bioMissing={bioMissing}
                onOpenCallProfile={() => setShowCallProfile(true)}
                onOpenTestAudio={() => setShowTestAudio(true)}
              />
              </div>
            );
            // A hairline between action groups.
            const sep = (
              <span aria-hidden className="h-5 w-px shrink-0 bg-slate-300/70 dark:!bg-white/15" />
            );
            return (
              <div className="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-200/80 pb-2.5 dark:border-white/10">
                <h2 className="mb-0 text-[19px] font-bold leading-tight tracking-[-0.01em] text-slate-800 dark:text-slate-100">
                  Sales Workspace
                </h2>
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  {tools}
                  {sep}
                  {icons}
                  <LegendKey view={view} />
                </div>
              </div>
            );
          })()}

          {/* The rule under the header (Nic 2026-10-23, like Onboarding). */}

          {board.data?.scheduleEmpty ? (
            <div className="mb-2 rounded-lg bg-amber-50 px-3 py-1.5 text-[11px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
              No Sales Reps are on today, so new leads land at the top of
              Day 1 for anyone to take.
            </div>
          ) : null}

          {/* CLOSED TODAY (Nic 2026-08-15). Nic opened the board on a Saturday
              and everything still looked alive. Nothing ticks any more, but a
              quiet board is ambiguous on its own — say plainly that it is
              closed and when it wakes up. Slate, not amber: being closed is a
              normal state, not a problem. */}
          {workCalendar && !workCalendar.isWorkingToday ? (
            <div className="mb-2 flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-[11px] font-medium text-slate-600 dark:!bg-white/5 dark:text-slate-300">
              <CalendarCog size={12} />
              {closedTodayLabel(workCalendar)} Leads still arrive and get
              assigned; their clocks wait until then.
            </div>
          ) : null}

          {/* Everyone scheduled is at lunch (or the only specialist is):
              the board pauses — calm violet, never an alarm. Leads still
              arrive and assign; their clocks stand still. */}
          {/* AWAY (Nic 2026-08-10 lunch, 2026-10-05 one "away" for lunch
              and meetings): the VIEWER's own banner while their clocks are
              paused, on every tab. Meetings pause from 5 minutes before to
              5 minutes after (server MEETING_PAUSE_PAD_MS). "I'm back" ends
              it now: clocks resume and new leads route to them again. Work
              goes on as normal while away; nothing is blocked. Takes the
              place of the board-wide lunch banner for them. */}
          {(() => {
            if (!board.data?.viewerUserId) return null;
            const t = now.getTime();
            const { list: pausesNow, lunch } = myPausesNow();
            // Back-to-back: the one that resumes last. The list carries
            // meetings and self-set breaks (kind 'away').
            const lastOf = (kind) =>
              pausesNow
                .filter((p) => (p.kind || 'meeting') === kind)
                .sort((a, b) => pauseEndMs(b) - pauseEndMs(a))[0];
            const meeting = lastOf('meeting');
            const selfSet = lastOf('away');
            const onCall = lastOf('call');
            const eating = !!lunch;
            if (!meeting && !selfSet && !eating && !onCall) return null;
            // A call is its own pause (2026-11-02): it ends when the call
            // does, so "I'm back" shows only when another pause is live.
            const callOnly = onCall && !meeting && !selfSet && !eating;
            let text;
            if (callOnly) {
              text =
                'On a call. Your clocks are paused and new leads skip you.';
            } else if (selfSet && !meeting) {
              const why =
                selfSet.reason === 'Other' && selfSet.note
                  ? selfSet.note
                  : selfSet.reason;
              text = `Away${why ? ` (${why})` : ''} until ${clockIn(new Date(selfSet.endAt))}. Your clocks are paused and new leads skip you.`;
            } else if (meeting) {
              const PAD = 5 * 60 * 1000;
              const startAt = meeting.meetingStartAt
                ? new Date(meeting.meetingStartAt)
                : new Date(new Date(meeting.startAt).getTime() + PAD);
              const booked = (meetingSummary.meetings || []).find(
                (m) =>
                  (meeting.calendlyBookingId &&
                    m.calendlyBookingId === meeting.calendlyBookingId) ||
                  Math.abs(new Date(m.startTime) - startAt) < 60 * 1000,
              );
              const who = booked
                ? [booked.leadFirstName, booked.leadLastName]
                    .filter(Boolean)
                    .join(' ')
                : '';
              const ended =
                booked?.endTime && t >= new Date(booked.endTime).getTime();
              const resume = clockIn(new Date(meeting.endAt));
              text = ended
                ? `Away: your meeting${who ? ` with ${who}` : ''} is over. Your clocks resume at ${resume}.`
                : `Away for your ${clockIn(startAt)} meeting${who ? ` with ${who}` : ''}. Your clocks resume at ${resume}.`;
            } else {
              text = `Away for lunch until ${clockIn(new Date(lunch.endAt))}. Your clocks are paused.`;
            }
            return (
              // Solid (Nic 2026-10-05: the tint didn't stand out).
              <div className="mb-2 flex items-center gap-2.5 rounded-lg bg-sky-600 px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm dark:!bg-sky-600">
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70 motion-reduce:animate-none" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
                </span>
                {callOnly ? (
                  <Phone size={16} className="shrink-0" />
                ) : meeting ? (
                  <CalendarClock size={16} className="shrink-0" />
                ) : selfSet ? (
                  <Coffee size={16} className="shrink-0" />
                ) : (
                  <Utensils size={16} className="shrink-0" />
                )}
                <span className="min-w-0 flex-1">{text}</span>
                {callOnly ? null : (
                  <button
                    type="button"
                    disabled={lunchCheckInMut.isLoading}
                    onClick={imBack}
                    title="End the pause now: your clocks resume and new leads route to you again"
                    className="shrink-0 whitespace-nowrap rounded-full bg-white px-3.5 py-1 text-[12px] font-bold text-sky-700 shadow-sm transition-colors hover:bg-sky-50 disabled:opacity-50 dark:!bg-white dark:!text-sky-700"
                  >
                    {lunchCheckInMut.isLoading ? 'One sec...' : "I'm back"}
                  </button>
                )}
              </div>
            );
          })()}

          {(() => {
            const sched = board.data?.todaySchedule || [];
            const allAtLunch =
              sched.length > 0 &&
              sched.every((p) =>
                isOnLunch(
                  { startAt: p.lunchStartAt, endAt: p.lunchEndAt },
                  now,
                ),
              );
            if (!allAtLunch) return null;
            // The eating viewer already has their own banner above.
            const mine = sched.find(
              (p) => p.userId === board.data?.viewerUserId,
            );
            if (
              mine &&
              isOnLunch(
                { startAt: mine.lunchStartAt, endAt: mine.lunchEndAt },
                now,
              )
            ) {
              return null;
            }
            const latestEnd = sched.reduce(
              (max, p) =>
                new Date(p.lunchEndAt).getTime() > max
                  ? new Date(p.lunchEndAt).getTime()
                  : max,
              0,
            );
            return (
              <div className="mb-2 flex items-center gap-2 rounded-lg bg-violet-50 px-3 py-1.5 text-[11px] font-medium text-violet-700 dark:bg-violet-500/10 dark:text-violet-400">
                <Utensils size={12} />
                Board paused for lunch until{' '}
                {new Date(latestEnd).toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                . Leads still arrive and assign; their clocks wait.
              </div>
            );
          })()}

          {/* View switch (Nic 2026-09-22): Making Contact | Potential
              Clients. Each side shows the OTHER side's live alert, so an
              arriving lead or a due follow-up is never hidden. */}
          <div className="mb-2.5 flex flex-col gap-2 lg:flex-row lg:items-center">
            <div
              ref={tabListRef}
              role="tablist"
              // Full width below lg (the tabs share it, the search sits
              // under it full width); fixed tabs beside the search from lg.
              className="relative flex w-full rounded-full bg-slate-200/70 p-1 dark:!bg-white/10 lg:inline-flex lg:w-auto"
            >
              {pill ? (
                <span
                  aria-hidden
                  className="absolute inset-y-1 rounded-full bg-white shadow-sm transition-[left,width] duration-300 ease-out motion-reduce:transition-none dark:!bg-surface-elevated"
                  style={{
                    left: pill.left,
                    width: pill.width,
                    transition: pill.animate ? undefined : 'none',
                  }}
                />
              ) : null}
              {[
                {
                  key: 'contact',
                  label: 'Making Contact',
                  count: makingContactDue,
                  alertTone: 'bg-red-500',
                  badgeTone: 'bg-red-500 text-white',
                },
                {
                  key: 'clients',
                  label: 'Signing Up',
                  count: potentialDue,
                  // Red, like the cards it counts (Nic 2026-10-06). No
                  // separate Details in badge: those cards are in the count.
                  alertTone: 'bg-red-500',
                  badgeTone: 'bg-red-500 text-white',
                },
                // Nurture (Nic 2026-09-28): a number only when someone needs
                // you (Nic 2026-10-23: was everyone parked). It's the ones who
                // reached out, sky and pulsing; nobody, no number.
                {
                  key: 'nurture',
                  label: 'Nurture',
                  // Anyone who can act may re-engage them: the shared pool.
                  count: tabsCanAct ? nurtureSummary.data?.detected || 0 : 0,
                  alert: tabsCanAct && (nurtureSummary.data?.detected || 0) > 0,
                  alertTone: 'bg-sky-500',
                  badgeTone: 'bg-sky-600 text-white',
                  quiet: true,
                },
                // Dropped (Nic 2026-10-15): the same, only who reached back out.
                {
                  key: 'dropped',
                  label: 'Dropped',
                  count: tabsCanAct ? droppedSummary.data?.detected || 0 : 0,
                  alert: tabsCanAct && (droppedSummary.data?.detected || 0) > 0,
                  alertTone: 'bg-sky-500',
                  badgeTone: 'bg-sky-600 text-white',
                  quiet: true,
                },
              ]
                .map((t) => ({
                  ...t,
                  hit: !!searchCounts && searchCounts[t.key].shown > 0,
                  found: searchCounts ? searchCounts[t.key] : null,
                }))
                .map((t) => (
                <button
                  key={t.key}
                  ref={(el) => {
                    tabRefs.current[t.key] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={view === t.key}
                  onClick={() => setView(t.key)}
                  className={`relative z-10 inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-1.5 sm:gap-2 sm:px-4 lg:w-40 lg:flex-none text-[13px] font-semibold outline-none transition-[color] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 ${
                    view === t.key
                      ? 'text-slate-900 dark:text-white'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  <span className="truncate">{t.label}</span>
                  {/* Quiet at zero, a colored badge when something needs
                      action; min width keeps the tab from shifting as the
                      number changes. The BADGE itself pulses (a halo behind
                      it, never a size change) when the other view needs you
                      (Nic 2026-09-22: no separate dot). No "0" on Making
                      Contact or Signing Up (Nic 2026-09-30), and no number on
                      any tab unless something needs you (Nic 2026-10-23),
                      unless a search hit needs the badge to glow. */}
                  <span
                    className={`relative inline-flex ${
                      t.count > 0 || t.found ? '' : 'hidden'
                    }`}
                  >
                    {t.hit ? (
                      <span
                        aria-hidden
                        className="absolute inset-0 animate-ping-soft rounded-full shadow-[0_0_0_1.5px_rgba(14,165,233,0.9)] motion-reduce:hidden"
                      />
                    ) : null}
                    {!t.found && (t.quiet ? t.alert : t.count > 0 && view !== t.key) ? (
                      <span
                        aria-hidden
                        className={`absolute inset-0 animate-ping rounded-full opacity-60 motion-reduce:hidden ${t.alertTone}`}
                      />
                    ) : null}
                    <span
                      className={`relative min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] tabular-nums transition-[background-color,color,box-shadow] ${
                        t.found
                          ? 'bg-sky-50 text-sky-700 dark:!bg-sky-500/15 dark:text-sky-200'
                          : (t.quiet ? t.alert : t.count > 0)
                          ? t.badgeTone
                          : t.hit
                            ? 'bg-sky-50 text-sky-700 dark:!bg-sky-500/15 dark:text-sky-200'
                            : 'bg-slate-900/[0.06] text-slate-500 dark:!bg-white/10 dark:text-slate-400'
                      } ${
                        t.hit
                          ? 'shadow-[0_0_0_1.5px_rgba(14,165,233,0.85),0_0_8px_rgba(14,165,233,0.3)]'
                          : ''
                      }`}
                    >
                      {t.found ? `${t.found.shown}/${t.found.total}` : t.count}
                    </span>
                  </span>
                  {t.ready > 0 ? (
                    <span
                      title={`${t.ready} confirmed their details and can be worked now`}
                      className="relative inline-flex"
                    >
                      {view !== t.key ? (
                        <span
                          aria-hidden
                          className="absolute inset-0 animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:hidden"
                        />
                      ) : null}
                      <span className="relative inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-1.5 text-[11px] font-bold tabular-nums text-white">
                        <Zap size={10} strokeWidth={3} />
                        {t.ready}
                      </span>
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            {/* How Signing Up works (Nic 2026-09-29): the desk's rules, one
                click away, as a right-hand panel (2026-10-29, every screen
                size). Signing Up tab only (Nic 2026-10-07). */}
            {view === 'clients' ? <SigningUpGuide /> : null}
            <div className="flex w-full items-center gap-2 lg:ml-auto lg:w-auto">
            {/* Making Contact only: the other tabs have no done tiles. */}
            {view === 'contact' ? (
              <HideDoneToggle on={hideDoneToday} hidden={hiddenDoneCount} onToggle={toggleHideDone} />
            ) : null}
            <div className="min-w-0 flex-1 lg:w-72 lg:flex-none">
              <PeopleSearch
                value={findText}
                onChange={setFindText}
                onOpenRun={openSearchResult}
                onOpenMatch={openPersonMatch}
                onAddPerson={
                  canWork
                    ? (typed) => setAddPerson({ prefill: prefillFromSearch(typed) })
                    : null
                }
              />
            </div>
            {/* Signing Up's Meetings button lands here, after the search. */}
            <div
              ref={setActionsSlot}
              className="flex shrink-0 items-center gap-2 empty:hidden"
            />
            </div>
          </div>

          {/* The same hairline under the tabs (Nic 2026-09-29), so the tab
              row sits between two rules like the header above it. */}
          <div className="mb-2.5 h-px w-full shrink-0 bg-slate-300/60 dark:!bg-white/15" />

          {/* The search is narrowing the board (Nic 2026-10-21): said once,
              on every tab, with a one-click clear, so a search left in the
              box never passes for "nothing to do". */}
          {searchCounts ? (
            <div
              role="status"
              data-testid="board-filter-bar"
              className="mb-2.5 flex shrink-0 items-center gap-2 rounded-lg border-l-4 border-sky-500 bg-sky-100 px-3 py-2 text-[13px] text-sky-900 shadow-sm ring-1 ring-sky-300 dark:border-sky-400 dark:!bg-sky-500/20 dark:text-sky-100 dark:ring-sky-400/40"
            >
              <Search size={15} strokeWidth={2.6} className="shrink-0 text-sky-600 dark:text-sky-300" />
              <span className="min-w-0 truncate">
                Filtered by <span className="font-semibold">“{findQ}”</span>
                <span className="mx-1.5 text-sky-500 dark:text-sky-400">·</span>
                Showing {searchCounts[view].shown} of {searchCounts[view].total}{' '}
                {searchCounts[view].total === 1 ? 'person' : 'people'} here
              </span>
              <button
                type="button"
                onClick={() => setFindText('')}
                className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-sky-700 dark:!bg-sky-500 dark:hover:!bg-sky-400 dark:text-slate-900"
              >
                Clear filter
                <X size={12} strokeWidth={2.6} />
              </button>
            </div>
          ) : null}

          {/* Only the open view (Nic 2026-10-20); it eases in from the side
              its tab is on (the tab motion above). overflow-hidden clips the
              28px nudge. */}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <div ref={viewBoxRef} className="flex h-full min-h-0 flex-col">
              {view === 'contact' ? (
              <section className="flex h-full min-h-0 flex-col pr-1">
                {/* The day pipeline: fluid columns on desktop, a vertically
                  scrolling stack of day sections below lg — nine columns cannot
                  share a narrow window, so the axis flips instead of squeezing. */}
                {/* Same quiet rule as above, vertically between the day columns.
                  Freestanding divider elements (not border utilities): a border
                  painted on a column's own edge disappears on the collapsed
                  quiet rails (white pill + rounded corners swallow it). */}
                <div className="os-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto min-[1360px]:flex-row min-[1360px]:overflow-visible">
                  {DAY_COLUMNS.map((column, i) => (
                    <React.Fragment key={column.key}>
                      {i > 0 ? (
                        <div
                          aria-hidden
                          className="hidden w-px shrink-0 self-stretch bg-slate-200/80 min-[1360px]:block dark:!bg-white/10"
                        />
                      ) : null}
                      <BoardColumn
                        column={column}
                        items={runsByColumn.get(column.key) || []}
                        reachedOut={reachOutsByColumn.get(column.key) || 0}
                        now={now}
                        cadenceVersion={cadenceVersion}
                        onOpenRun={(run) =>
                          setSelected({ type: 'run', id: run.cadenceRunId })
                        }
                        slim={
                          column.collapsible && !expandedQuiet.has(column.key)
                        }
                        onToggleSlim={() => toggleQuiet(column.key)}
                        filtered={!!findQ}
                        // Intake lives at the top of Day 1 (Nic 2026-10-21):
                        // the same leads and clock, no row of their own.
                        arrivals={column.key === 'day1' ? intake : []}
                        newSection={column.key === 'day1'}
                        showOwners={canManage || !!board.data?.viewOnly}
                        onOpenArrival={(lead) =>
                          setSelected({ type: 'intake', id: lead.leadId })
                        }
                        arrivalPausesFor={intakePausesFor}
                        // Tile clocks pause/mark around the run OWNER's lunch
                        // and meetings (Nic 2026-09-25).
                        ownerLunchFor={(run) => {
                          const entry = scheduleNow.find(
                            (p) => p.userId === run.ownerUserId,
                          );
                          return entry
                            ? ownerPausesOf(entry)
                            : fallbackPausesOf(
                                board.data?.defaultLunch,
                                board.data?.ownerMeetingPauses?.[run.ownerUserId],
                              );
                        }}
                      />
                    </React.Fragment>
                  ))}
                </div>
              </section>
              ) : null}
              {view === 'clients' ? (
              <section className="flex h-full min-h-0 flex-col px-1">
                {/* Signing Up's desk (Nic 2026-10-21): the Onboarding
                    Workspace's Clients tab, for leads. */}
                <SigningUpDesk
                  runs={contactRuns}
                  now={now}
                  canAct={canWork}
                  pinned={board.data?.prefs?.suPins || []}
                  hideDone={!!board.data?.prefs?.suHideDone}
                  scoreboardFolded={!!board.data?.prefs?.suScoreboardFolded}
                  closedUntil={closedUntil}
                  directSignupCount={potential.data?.directSignupCount || 0}
                  detailsReady={potential.data?.detailsReady || []}
                  directSignupReadyCount={potential.data?.directSignupReadyCount || 0}
                  onOpenRun={openSigningUpRun}
                  onNudgeRun={canWork ? (run) => setNudgeRun(run) : null}
                  viewerUserId={board.data?.viewerUserId}
                  showOwners={canManage || !!board.data?.viewOnly}
                  onOpenDirectSignups={() => setDirectSignupsOpen(true)}
                  onOpenMeeting={openMeetingLead}
                  onOpenCalendar={() => setMeetingsOpen(true)}
                  signedToday={potential.data?.signedToday || []}
                  flashLeadId={flashLeadId}
                  search={findQ}
                  headerSlots={actionsSlot ? { actions: actionsSlot } : null}
                  active={view === 'clients'}
                />
              </section>
              ) : null}
              {view === 'nurture' ? (
              <section className="flex h-full min-h-0 flex-col pl-1">
                <NurtureView
                  keep={{ state: nurtureKeep, setState: setNurtureKeep }}
                  q={findQ}
                  focus={nurtureFocus}
                  canWork={canWork}
                  viewer={viewerForLists}
                  reps={board.data?.salesReps || []}
                  onPulledIn={(leadId) => {
                    setView('clients');
                    setFlashLeadId(leadId);
                  }}
                />
              </section>
              ) : null}
              {view === 'dropped' ? (
              <section className="flex h-full min-h-0 flex-col pl-1">
                <DroppedView
                  keep={{ state: droppedKeep, setState: setDroppedKeep }}
                  q={findQ}
                  focus={droppedFocus}
                  canWork={canWork}
                  viewer={viewerForLists}
                  reps={board.data?.salesReps || []}
                  onReengaged={(leadId) => {
                    setView('clients');
                    setFlashLeadId(leadId);
                  }}
                />
              </section>
              ) : null}
            </div>
          </div>
        </div>

        <CallGuideEditor
          open={showCallGuide}
          onClose={() => setShowCallGuide(false)}
          guide={board.data?.callGuide || null}
        />

        <NudgeComposer
          open={!!nudgeRun}
          lead={nudgeRun?.lead}
          onClose={() => setNudgeRun(null)}
        />

        <OffBoardMeetingDialog
          meeting={offBoardMeeting}
          viewerUserId={board.data?.viewerUserId}
          onClose={() => setOffBoardMeeting(null)}
        />
        <MeetingsCalendar
          open={meetingsOpen}
          onClose={() => setMeetingsOpen(false)}
          now={now}
          // Managers, admins and view only: everyone, filter by host; reps
          // see their own (Nic 2026-10-11: no Mine / Everyone toggle).
          seesAll={canManage || !!board.data?.viewOnly}
          viewerUserId={board.data?.viewerUserId}
          onOpen={openMeetingLead}
          onCall={callMeetingLead}
          canCall={meetingIsMine}
        />

        <DirectSignupsPanel
          open={directSignupsOpen}
          canClaim={canWork}
          onClose={() => setDirectSignupsOpen(false)}
          onClaimed={(res) => {
            // Claiming is not a call (Nic 2026-09-23): the signup lands in the
            // list, ringed for a moment, and the rep calls when ready.
            setDirectSignupsOpen(false);
            if (res?.leadId) {
              setView('clients');
              setFlashLeadId(res.leadId);
            }
          }}
        />

        <NotYoursDialog person={notYours} onClose={() => setNotYours(null)} />
        <AddPersonDialog
          open={!!addPerson}
          prefill={addPerson?.prefill}
          viewerUserId={board.data?.viewerUserId}
          owners={canManage ? reassignSpecialists : null}
          onClose={() => setAddPerson(null)}
          onOpenMatch={openPersonMatch}
          // Added (Nic 2026-10-21): no after-screen. Signing Up shows, and
          // their card rings so the rep opens their Workspace from there.
          onDone={(res) => {
            setAddPerson(null);
            setFindText('');
            setView('clients');
            if (res?.leadId) setFlashLeadId(res.leadId);
            board.refetch();
          }}
        />
        <LeadSlideOver
          open={!!(selectedIntakeLead || selectedRun)}
          onClose={() => setSelected(null)}
          intakeLead={selectedIntakeLead}
          run={selectedRun}
          now={now}
          cadenceVersion={cadenceVersion}
          scripts={board.data?.scripts || {}}
          ownerLunch={(() => {
            if (!selectedRun) return null;
            const entry = scheduleNow.find(
              (p) => p.userId === selectedRun.ownerUserId,
            );
            return entry
              ? ownerPausesOf(entry)
              : fallbackPausesOf(
                  board.data?.defaultLunch,
                  board.data?.ownerMeetingPauses?.[selectedRun.ownerUserId],
                );
          })()}
          user={user}
          onOpenBio={() => setShowCallProfile(true)}
          onTake={handleTake}
          onCall={(step) => {
            const run = runById.get(step.cadenceRunId) || selectedRun;
            if (!run) return;
            callCenter.startStepCall({
              run,
              step,
              boardContext: {
                transferTargets: board.data?.transferTargets || [],
                salesReps: board.data?.salesReps || [],
                viewerUserId: board.data?.viewerUserId,
                orgCalendlyUrl: board.data?.orgCalendlyUrl || '',
                callGuide: board.data?.callGuide || null,
                scripts: board.data?.scripts || {},
              },
            });
          }}
          onTextNudge={(run) => setNudgeRun(run)}
          boardContext={meetingBoardContext()}
          onFollowUpCall={(run) => {
            callCenter.startFollowUpCall({
              run,
              boardContext: {
                transferTargets: board.data?.transferTargets || [],
                salesReps: board.data?.salesReps || [],
                viewerUserId: board.data?.viewerUserId,
                orgCalendlyUrl: board.data?.orgCalendlyUrl || '',
                callGuide: board.data?.callGuide || null,
                scripts: board.data?.scripts || {},
              },
            });
          }}
          onOutsideCadenceCall={(run) => {
            // The slide-over STAYS open (Nic 2026-08-13) — same as the
            // Contact Made "Call them now" behavior.
            callCenter.startOutsideCadenceCall({
              run,
              boardContext: {
                transferTargets: board.data?.transferTargets || [],
                salesReps: board.data?.salesReps || [],
                viewerUserId: board.data?.viewerUserId,
                orgCalendlyUrl: board.data?.orgCalendlyUrl || '',
                callGuide: board.data?.callGuide || null,
                scripts: board.data?.scripts || {},
              },
            });
          }}
          onManualComplete={handleManualComplete}
          onSendSms={handleSendSms}
          onSendEmail={handleSendEmail}
          onContactMade={(via) => handleContactMade(via)}
          onConfirmDetection={(via, leadOutreachId) =>
            handleContactMade(via, leadOutreachId)
          }
          onDismissDetections={async (leadId) => {
            await ackDetections.mutateAsync({ leadId });
            board.refetch();
          }}
          onOutcome={handleOutcome}
          onDrop={handleDrop}
          onDismissStop={handleDismissStop}
          onStartNow={handleStartNow}
          busy={busy}
          callDocked={callCenter.dockVisible}
          reassignSpecialists={reassignSpecialists}
          onReassignRun={(cadenceRunId, toUserId) =>
            reassignRun.mutate({ cadenceRunId, toUserId })
          }
          onReassignIntake={(leadId, toUserId) =>
            reassignIntake.mutate({ leadId, toUserId })
          }
          onPushLead={handlePushLead}
          onUnpushLead={handleUnpushLead}
        />

        <ScheduleCalendar
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
        />

        {eventsOpen ? (
          <EventsPanel onClose={() => setEventsOpen(false)} />
        ) : null}


        {/* The dialer, the in-call flow, and the post-call prompts all live
            in the app-level Call Command Center now (2026-08-12) — see
            callCenter/CallCenterProvider. */}
        <CallProfileEditor
          show={showCallProfile}
          onHide={() => setShowCallProfile(false)}
        />
        <TestAudioModal
          show={showTestAudio}
          onHide={() => setShowTestAudio(false)}
        />
      </BoardContexts>
    </PrivateLayout>
  );
};

export default BoardPage;
